import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, Building, Calendar, FileDown, Settings, LogOut, CheckCircle, 
  XCircle, AlertTriangle, Clock, FileText, BarChart2, Plus, Upload, 
  Home, Edit, Trash2, Code, User, Check, Send, Printer, Menu, 
  MessageCircle, CheckSquare, Square, Key, Save, X 
} from 'lucide-react';

// ==========================================
// 0. 全域 API 網址設定
// ==========================================
const API_BASE_URL = 'https://shl1-atdaudit-n8n.zeabur.app/webhook';

// ==========================================
// 1. Initial Database State
// ==========================================
const INITIAL_DB = { 
  GSCORP: [], 
  GSDEPT: [], 
  GSUSER: [], 
  GSHOLIDAY: [], 
  PERREC: [], 
  MPUNCHH: [], 
  MPUNCHD: [] 
};

// ==========================================
// 2. Utility Functions
// ==========================================
const pick = (obj, keys, fallback = '') => {
  if (!obj) return fallback;
  const objKeys = Object.keys(obj);
  
  for (const key of keys) {
    const matchKey = objKeys.find(k => k.toLowerCase() === key.toLowerCase());
    if (matchKey) {
      const val = obj[matchKey];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return typeof val === 'string' ? String(val).trim() : val;
      }
    }
  }
  return fallback;
};

const normalizeDate = (value) => {
  if (!value) return '';
  let str = String(value).trim().split('T')[0].replace(/\//g, '-');
  
  if (/^\d{1,2}-\d{1,2}(?!\d)/.test(str)) {
     const parts = str.split('-');
     const y = new Date().getFullYear();
     const m = parts[0].replace(/\D/g, '');
     const d = parts[1].replace(/\D/g, '').substring(0, 2);
     return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  
  if (/^\d{3,4}-\d{1,2}-\d{1,2}/.test(str)) {
     const parts = str.split('-');
     let y = parts[0].replace(/\D/g, '');
     if (y.length === 3) y = String(parseInt(y, 10) + 1911);
     const m = parts[1].replace(/\D/g, '');
     const d = parts[2].replace(/\D/g, '').substring(0, 2);
     return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  
  return str.substring(0, 10); 
};

const normalizeTime = (value) => {
  if (!value) return '';
  const str = String(value).trim();
  if (!str || str === 'null' || str === 'NULL') return '';
  if (str.includes('T')) {
    const timePart = str.split('T')[1] || '';
    return timePart.substring(0, 8);
  }
  const m = str.match(/\d{2}:\d{2}(:\d{2})?/);
  return m ? m[0] : str;
};

const normalizeApprovalFlag = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'y' || normalized === '1' || normalized === 'true';
};

const getExpectedIn = (row) =>
  normalizeTime(pick(row, ['RSTime_start', 'RSTimestart', 'RSTimeStart'], ''));

const getExpectedOut = (row) =>
  normalizeTime(pick(row, ['RSTime_end', 'RSTimeend', 'RSTimeEnd'], ''));

const getActualIn = (row) =>
  normalizeTime(pick(row, ['ASTime_start', 'ASTimestart', 'ASTimeStart'], ''));

const getActualOut = (row) =>
  normalizeTime(pick(row, ['ASTime_end', 'ASTimeend', 'ASTimeEnd'], ''));

const timeToMinutes = (value) => {
  const s = String(value || '').trim();
  if (!s || s === '00:00' || s === '00:00:00' || s === '------') return null;
  const parts = s.split(':');
  if (parts.length < 2) return null;
  const hh = Number(parts[0]);
  const mm = Number(parts[1]);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

const buildCombinedAbnormalStatus = (row, baseStatus = '') => {
  const rawStatus = String(baseStatus || pick(row, ['Rcode'], '') || '').trim().replace(/未刷卡/g, '未打卡');

  if (rawStatus.includes('已核准') || rawStatus.includes('已審核') || rawStatus.includes('核准')) return '已核准';
  if (rawStatus.includes('審核中') || rawStatus.includes('待審核中') || rawStatus.includes('申請中')) return '審核中';

  const expectedIn = getExpectedIn(row);
  const expectedOut = getExpectedOut(row);
  const actualIn = getActualIn(row);
  const actualOut = getActualOut(row);

  const expectedInMins = timeToMinutes(expectedIn);
  const expectedOutMins = timeToMinutes(expectedOut);
  const actualInMins = timeToMinutes(actualIn);
  const actualOutMins = timeToMinutes(actualOut);

  const hasIn = actualInMins != null;
  const hasOut = actualOutMins != null;
  const hasAnyPunch = hasIn || hasOut;
  const hasExpectedIn = expectedInMins != null;
  const hasExpectedOut = expectedOutMins != null;
  const hasExpectedPunch = hasExpectedIn || hasExpectedOut;
  const isLate = hasIn && hasExpectedIn && actualInMins >= expectedInMins + 5;
  const isEarlyLeave = hasOut && hasExpectedOut && actualOutMins < expectedOutMins;

  if (!hasExpectedPunch) {
    if (hasIn && hasOut) {
      if (actualInMins >= 8 * 60 + 5 && actualOutMins < 17 * 60 + 30) return '遲到、早退';
      if (actualInMins >= 8 * 60 + 5) return '遲到';
      if (actualOutMins < 17 * 60 + 30) return '早退';
      return rawStatus || '正常';
    }
    if (!hasIn && hasOut) return actualOutMins < 17 * 60 + 30 ? '未打卡、早退' : '未打卡';
    if (hasIn && !hasOut) return actualInMins >= 8 * 60 + 5 ? '遲到、未打卡' : '未打卡';
    return rawStatus || '';
  }

  if (!hasAnyPunch && hasExpectedPunch) return '未打卡';
  if (hasIn && !hasOut) return isLate ? '遲到、未打卡' : '未打卡';
  if (!hasIn && hasOut) return isEarlyLeave ? '未打卡、早退' : '未打卡';
  if (isLate && isEarlyLeave) return '遲到、早退';
  if (isLate) return '遲到';
  if (isEarlyLeave) return '早退';

  return rawStatus || '正常';
};

const isMissingPunchRow = (row) => {
  const expectedIn = getExpectedIn(row);
  const expectedOut = getExpectedOut(row);
  const actualInRaw = getActualIn(row);
  const actualOutRaw = getActualOut(row);

  const actualIn = actualInRaw && actualInRaw !== '------' ? actualInRaw : '';
  const actualOut = actualOutRaw && actualOutRaw !== '------' ? actualOutRaw : '';

  const hasExpectedPunch = !!expectedIn || !!expectedOut;
  return hasExpectedPunch && (!actualIn || !actualOut);
};

const buildPunchDetailMap = (db) => {
  const headers = db.MPUNCHH || [];
  const details = db.MPUNCHD || [];
  const map = {};

  headers.forEach((h) => {
    const serno = String(pick(h, ['Serno'])).trim();
    const userId = String(pick(h, ['Code', 'UserId'])).trim();
    
    const hSignSvisor = pick(h, ['SignSvisor', 'Signvisor']);

    if (!serno || !userId) return;

    const rows = details.filter(
      (d) => String(pick(d, ['Serno'])).trim() === serno
    );

    rows.forEach((d) => {
      const sdate = normalizeDate(pick(d, ['Sdate']));
      if (!sdate) return;

      if (!map[userId]) map[userId] = {};

      map[userId][sdate] = {
        serno,
        sno: pick(d, ['Sno']),
        sdate,
        noSwipeStart: normalizeTime(pick(d, ['NoSwipe_start', 'NoSwipeStart'])),
        noSwipeEnd: normalizeTime(pick(d, ['NoSwipe_end', 'NoSwipeEnd'])),
        reason: pick(d, ['Reason']),
        signSvisor: hSignSvisor,
        signCheck: pick(d, ['Sign_check'])
      };
    });
  });

  details.forEach((d) => {
    const userId = String(pick(d, ['UserId', 'Code'])).trim();
    const sdate = normalizeDate(pick(d, ['Sdate']));
    if (!userId || !sdate) return;

    if (!map[userId]) map[userId] = {};

    if (!map[userId][sdate]) {
      map[userId][sdate] = {
        serno: pick(d, ['Serno']),
        sno: pick(d, ['Sno']),
        sdate,
        noSwipeStart: normalizeTime(pick(d, ['NoSwipe_start', 'NoSwipeStart'])),
        noSwipeEnd: normalizeTime(pick(d, ['NoSwipe_end', 'NoSwipeEnd'])),
        reason: pick(d, ['Reason']),
        signSvisor: pick(d, ['SignSvisor', 'Signvisor']),
        signCheck: pick(d, ['Sign_check'])
      };
    }
  });

  return map;
};

const mergePerrecWithPunchStatus = (db) => {
  const punchMap = buildPunchDetailMap(db);

  return (db.PERREC || []).map((record) => {
    const userId = String(pick(record, ['Code'])).trim();
    const sdate = normalizeDate(pick(record, ['Sdate']));
    const punch = punchMap[userId]?.[sdate];

    const rawRcode = String(pick(record, ['Rcode'], ''));

    if (!punch) return { ...record, _rawRcode: rawRcode };

    let nextRcode = rawRcode;
    const approved = normalizeApprovalFlag(punch.signCheck);

    if (approved) {
      if (nextRcode.includes('審核中')) {
        nextRcode = nextRcode.replace(/審核中/g, '已核准');
      } else if (!nextRcode.includes('已核准')) {
        nextRcode = nextRcode ? `${nextRcode}、已核准` : '已核准';
      }
    } else {
      if (!nextRcode.includes('已核准')) {
        if (nextRcode.includes('未打卡') || nextRcode.includes('未刷卡')) {
          nextRcode = nextRcode.replace(/未打卡/g, '審核中').replace(/未刷卡/g, '審核中');
        } else if (!nextRcode.includes('審核中')) {
          nextRcode = nextRcode ? `${nextRcode}、審核中` : '審核中';
        }
      }
    }

    return {
      ...record,
      _rawRcode: rawRcode,
      Rcode: nextRcode,
      _serno: punch.serno,
      _sno: punch.sno,
      _makeupTimeStart: punch.noSwipeStart || '', 
      _makeupTimeEnd: punch.noSwipeEnd || '',     
      _reason: punch.reason || '',
      _signSvisor: punch.signSvisor || '',
      _signCheck: punch.signCheck || ''
    };
  });
};

const getPunchBuckets = (db, currentUser) => {
  if (!currentUser) {
    return { availablePunches: [], submittedPunches: [], latestMonth: '' };
  }

  const userId = String(currentUser?.UserId || '').trim();
  const punchMap = buildPunchDetailMap(db);
  const userPunchMap = punchMap[userId] || {};

  const userRecords = db.PERREC
    .filter(r => String(pick(r, ['Code'], '')).trim() === userId)
    .sort((a, b) => new Date(normalizeDate(pick(a, ['Sdate'], ''))) - new Date(normalizeDate(pick(b, ['Sdate'], ''))));

  let latestMonth = '';
  if (userRecords.length > 0) {
    const lastRecordDate = normalizeDate(pick(userRecords[userRecords.length - 1], ['Sdate'], ''));
    if (lastRecordDate) latestMonth = lastRecordDate.substring(0, 7);
  }

  const availablePunches = [];
  const submittedPunches = [];

  userRecords.forEach((row, index) => {
    const sdate = normalizeDate(pick(row, ['Sdate'], ''));
    if (!sdate) return;

    const status = buildCombinedAbnormalStatus(row, String(pick(row, ['Rcode'], '')).trim());
    const punch = userPunchMap[sdate];
    const cvertcheck = String(pick(row, ['Cvertcheck', 'CvertCheck'], '')).toUpperCase();
    const isCvertchecked = cvertcheck === 'Y' || cvertcheck === '1' || cvertcheck === 'TRUE';

    const hasPunchRecord = !!punch;
    const isMissing = status.includes('未打卡');
    const isPending = status.includes('申請中') || status.includes('審核中');
    const isApproved = status.includes('核准');

    const expectedIn = getExpectedIn(row);
    const expectedOut = getExpectedOut(row);
    const actualIn = getActualIn(row) || normalizeTime(pick(row, ['ASTime_start', 'ASTimeStart', 'ASTimestart'], '')) || '------';
    const actualOut = getActualOut(row) || normalizeTime(pick(row, ['ASTime_end', 'ASTimeEnd', 'ASTimeend'], '')) || '------';

    const makeupTimestart = punch?.noSwipeStart || pick(row, ['makeupTimeStart'], '');
    const makeupTimeend = punch?.noSwipeEnd || pick(row, ['makeupTimeEnd'], '');
    const reason = punch?.reason || pick(row, ['reason', 'Reason'], '');
    const signCheck = String(
      punch?.signCheck ?? pick(row, ['signCheck', 'Signcheck'], '')
    ).trim();

    const punchApproved = normalizeApprovalFlag(signCheck);

    // 先清理 status，移除已有的「審核中」「申請中」避免重複，再重新組合
    let displayStatus = status.replace(/,+/g, ',').replace(/^,|,$/g, '').trim();
    displayStatus = displayStatus.replace(/、?審核中/g, '').replace(/、?申請中/g, '').replace(/^、|、$/g, '').trim();
    if (isMissing && !displayStatus.includes('未打卡')) {
      displayStatus = displayStatus ? `${displayStatus}、未打卡` : '未打卡';
    }
    if (punchApproved && !displayStatus.includes('核准')) {
      displayStatus = '核准';
    } else if (hasPunchRecord && !punchApproved) {
      displayStatus = displayStatus ? `${displayStatus}、審核中` : '審核中';
    }

    const baseItem = {
      id: `${sdate}-${index}`,
      date: sdate,
      expectedIn,
      expectedOut,
      actualIn,
      actualOut,
      status: displayStatus,
      makeupTimestart,
      makeupTimeend,
      reason,
      serno: punch?.serno || pick(row, ['serno', 'Serno'], ''),
      sno: punch?.sno || pick(row, ['sno', 'Sno'], ''),
      signSvisor: punch?.signSvisor || pick(row, ['signSvisor'], ''),
      isApproved: displayStatus.includes('核准'),
      isSubmitted: hasPunchRecord || isPending || isApproved
    };

    if (isMissing && !baseItem.isSubmitted && !isCvertchecked) {
      availablePunches.push({
        ...baseItem,
        makeupTimestart: '',
        makeupTimeend: '',
        reason: '',
        isApproved: false,
        isSubmitted: false
      });
    }

    if (hasPunchRecord || isPending || isApproved) {
      submittedPunches.push(baseItem);
    }
  });

  submittedPunches.sort((a, b) => new Date(a.date) - new Date(b.date));

  return { availablePunches, submittedPunches, latestMonth };
};

const getApprovalBuckets = (db, manager) => {
  if (!manager) return [];
  
  const isSupervisor = manager.Supervisor || manager.Supervisor === 1 || String(manager.Supervisor) === '1';
  const isManager = manager.Manager || manager.Manager === 1 || String(manager.Manager) === '1';
  const isGM = manager.GM || manager.GM === 1 || String(manager.GM) === '1';

  if (!isSupervisor && !isManager && !isGM) return [];

  const eligibleUsers = (db.GSUSER || []).filter(applicant => {
    if (String(applicant.UserId).trim() === String(manager.UserId).trim()) return false;
    if (String(manager.Corpcode).trim() !== String(applicant.Corpcode).trim()) return false;

    const appDept = String(applicant.Deptcode || '').trim().toUpperCase();
    const apprDept = String(manager.Deptcode || '').trim().toUpperCase();

    const isAppGeneral = !applicant.Supervisor && !applicant.Manager && !applicant.GM;
    const isAppSupervisor = !!applicant.Supervisor || applicant.Supervisor === 1 || String(applicant.Supervisor) === '1';
    const isAppManager = !!applicant.Manager || applicant.Manager === 1 || String(applicant.Manager) === '1';

    if (appDept === 'BD01') {
      if (isAppManager) return isGM; 
      return isGM;
    }

    if (appDept === 'BD02') {
      if (isAppManager) return isGM; 
      return isManager; 
    }

    if (appDept === 'BD03') {
      if (isAppManager) return isGM;
      if (isAppGeneral) return isSupervisor && apprDept === 'BD03';
      if (isAppSupervisor) return isManager; 
      return false;
    }

    if (appDept.startsWith('B')) {
      if (isAppManager) return isGM;
      if (isAppGeneral) return isSupervisor && apprDept === appDept;
      if (isAppSupervisor) return isManager; 
      return false;
    }

    if (isAppGeneral) return isSupervisor && apprDept === appDept;
    if (isAppSupervisor) return isManager && apprDept === appDept;
    if (isAppManager) return isGM;

    return false;
  });

  const eligibleUserIds = eligibleUsers.map(u => String(u.UserId).trim());
  const punchMap = buildPunchDetailMap(db);
  const approvals = [];

  (db.PERREC || []).forEach((row, index) => {
    const userId = String(pick(row, ['Code'])).trim();
    if (!eligibleUserIds.includes(userId)) return;

    const sdate = normalizeDate(pick(row, ['Sdate']));
    if (!sdate) return;

    const punch = punchMap[userId]?.[sdate];
    if (!punch) return;

    const status = String(pick(row, ['Rcode'], '') || '').trim();
    const expectedIn = normalizeTime(pick(row, ['RSTimestart', 'RSTime_start'], ''));
    const expectedOut = normalizeTime(pick(row, ['RSTimeend', 'RSTime_end'], ''));
    const actualIn = normalizeTime(pick(row, ['ASTimestart', 'ASTime_start'], '')) || '------';
    const actualOut = normalizeTime(pick(row, ['ASTimeend', 'ASTime_end'], '')) || '------';
    
    const makeupTime_start = punch.noSwipeStart || '';
    const makeupTime_end = punch.noSwipeEnd || '';

    const reason = punch.reason || '';
    const signCheck = punch.signCheck || '';
    const punchApproved = normalizeApprovalFlag(signCheck);
    
    const isApproved = status.includes('已核准') || punchApproved;
    const displayStatus = isApproved ? '已核准' : '審核中';

    const userObj = eligibleUsers.find(u => String(u.UserId).trim() === userId) || {};

    approvals.push({
      id: `${userId}-${sdate}-${index}`,
      userId: userId,
      userName: userObj.Name || userId,
      deptCode: userObj.Deptcode,
      date: sdate,
      expectedIn,
      expectedOut,
      actualIn,
      actualOut,
      status: displayStatus,
      makeupTime_start,
      makeupTime_end,
      reason,
      serno: punch.serno,
      sno: punch.sno,
      signSvisor: punch.signSvisor,
      isApproved: isApproved
    });
  });

  approvals.sort((a, b) => new Date(b.date) - new Date(a.date));
  return approvals;
};

const buildNextPunchSerno = (db, currentUser) => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const prefix = `${yyyy}${mm}${dd}`; 

  const todaySernos = (db.MPUNCHH || [])
    .map(h => String(pick(h, ['Serno'])).trim())
    .filter(s => s.startsWith(prefix));

  let nextSeq = 1;
  if (todaySernos.length > 0) {
    const maxSeq = Math.max(...todaySernos.map(s => {
      const seqStr = s.substring(8); 
      const seqNum = parseInt(seqStr, 10);
      return isNaN(seqNum) ? 0 : seqNum;
    }));
    nextSeq = maxSeq + 1;
  }
  return `${prefix}${String(nextSeq).padStart(3, '0')}`;
};

const sanitizeMpunchHeaderForSync = (payload) => ({
  CorpSno: Number(pick(payload, ['CorpSno']) || 0),
  Serno: String(pick(payload, ['Serno']) || '').trim(),
  Code: String(pick(payload, ['Code', 'UserId']) || '').trim(),
  Deptcode: String(pick(payload, ['Deptcode']) || '').trim(),
  SignSvisor: String(pick(payload, ['SignSvisor', 'Signvisor']) || '').trim(),
  EmpCheck: normalizeApprovalFlag(pick(payload, ['EmpCheck'])) ? 1 : 0
});

const sanitizeMpunchDetailForSync = (row) => {
  const rawStart = row.hasOwnProperty('NoSwipe_start') ? row.NoSwipe_start : pick(row, ['NoSwipe_start', 'NoSwipeStart']);
  const rawEnd = row.hasOwnProperty('NoSwipe_end') ? row.NoSwipe_end : pick(row, ['NoSwipe_end', 'NoSwipeEnd']);
  
  const formatTimeForSQL = (t) => {
    if (t === null || t === undefined || String(t).trim() === '' || String(t).trim() === 'null') return null;
    const str = String(t).trim();
    if (str.length === 5 && str.includes(':')) return `${str}:00`;
    return str;
  };

  const nsStart = formatTimeForSQL(rawStart);
  const nsEnd = formatTimeForSQL(rawEnd);
  const reason = String(row.Reason || pick(row, ['Reason', 'REASON']) || '').trim();
  const signCheck = normalizeApprovalFlag(row.Sign_check ?? pick(row, ['Sign_check', 'sign_check'])) ? 1 : 0;

  return {
    CorpSno: Number(pick(row, ['CorpSno']) || 0),
    Serno: String(pick(row, ['Serno']) || '').trim(),
    Sno: Number(pick(row, ['Sno']) || 0),
    Sdate: normalizeDate(pick(row, ['Sdate'])),
    NoSwipe_start: nsStart,
    NoSwipeStart: nsStart, 
    NoSwipe_end: nsEnd,
    NoSwipeEnd: nsEnd,
    Reason: reason,
    REASON: reason,
    Sign_check: signCheck,
    sign_check: signCheck
  };
};

const sanitizePerrecForSync = (record) => {
  const cv = String(pick(record, ['Cvertcheck'])).toUpperCase();
  const isChecked = cv === 'Y' || cv === '1' || cv === 'TRUE' || record.Cvertcheck === 1 || record.Cvertcheck === true;
  return {
    Code: String(pick(record, ['Code'], '')).trim(),
    Sdate: normalizeDate(pick(record, ['Sdate'], '')),
    Rcode: String(pick(record, ['Rcode'], '')).trim(),
    Cvertcheck: isChecked ? 1 : 0
  };
};

const getApproverIds = (db, applicant) => {
  const applicantDeptName = db.GSDEPT?.find((d) => pick(d, ['Code']) === applicant.Deptcode)?.Name || '';
  const isGeneral = !applicant.Supervisor && !applicant.Manager && !applicant.GM;
  const isSupervisor = applicant.Supervisor === 1 || applicant.Supervisor === true || String(applicant.Supervisor) === '1';
  let targets = [];

  if (applicantDeptName.includes('管理部')) targets = db.GSUSER.filter((u) => u.GM);
  else if ((applicantDeptName.includes('工務') && isGeneral) || (applicantDeptName.includes('業務') && isSupervisor) || (applicantDeptName.includes('工地') && isSupervisor)) targets = db.GSUSER.filter((u) => u.Manager);
  else if (isGeneral) {
    targets = db.GSUSER.filter((u) => u.Supervisor && u.Deptcode === applicant.Deptcode);
    if (targets.length === 0) targets = db.GSUSER.filter((u) => u.Manager);
  }
  else targets = db.GSUSER.filter((u) => u.GM);

  return [...new Set(targets.filter((u) => u.UserId !== applicant.UserId).map((u) => String(u.UserId).trim()))];
};

// ==========================================
// 7. MAIN APP COMPONENT
// ==========================================
export default function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    try { const saved = sessionStorage.getItem('ams_user'); return saved ? JSON.parse(saved) : null; } 
    catch { return null; }
  });
  const [isAdminMode, setIsAdminMode] = useState(() => {
    return sessionStorage.getItem('ams_admin') === 'true';
  });
  const [view, setView] = useState(() => {
    return sessionStorage.getItem('ams_view') || (sessionStorage.getItem('ams_user') ? 'dashboard' : 'login');
  });
  
  const [db, setDb] = useState(INITIAL_DB);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [showDeleteAuthModal, setShowDeleteAuthModal] = useState(false);
  const [deleteAuthPwd, setDeleteAuthPwd] = useState('');

  useEffect(() => {
    document.title = "AMS補打卡申請審核作業";
  }, []);

  useEffect(() => {
    if (currentUser) sessionStorage.setItem('ams_user', JSON.stringify(currentUser));
    else sessionStorage.removeItem('ams_user');
  }, [currentUser]);

  useEffect(() => { sessionStorage.setItem('ams_admin', isAdminMode); }, [isAdminMode]);
  useEffect(() => { sessionStorage.setItem('ams_view', view); }, [view]);

  const activeUser = React.useMemo(() => {
    if (!currentUser) return null;
    const dbUser = db.GSUSER?.find(u => String(u.UserId).trim() === String(currentUser.UserId).trim());
    if (dbUser) {
      return {
        ...currentUser,
        ...dbUser,
        Name: (dbUser.Name && dbUser.Name !== '未提供姓名') ? dbUser.Name : currentUser.Name,
      };
    }
    return currentUser;
  }, [currentUser, db.GSUSER]);

  const isManagerMode = isAdminMode || 
                        activeUser?.Supervisor || activeUser?.Supervisor === 1 || activeUser?.Supervisor === '1' || 
                        activeUser?.Manager || activeUser?.Manager === 1 || activeUser?.Manager === '1' || 
                        activeUser?.GM || activeUser?.GM === 1 || activeUser?.GM === '1';

  const syncWithDatabase = async (tableName, action, payload) => {
    const adminTables = ['GSCORP', 'GSDEPT', 'GSUSER', 'GSHOLIDAY'];
    const isAdminTable = adminTables.includes(tableName);
    
    const dbType = isAdminTable ? 'admin-db' : 'manage-db';
    const endpoint = `${API_BASE_URL}/${dbType}`;

    console.log(`🚀 [發送至 ${dbType}] -> 請求更新表單 ${tableName}`);
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tableName, action, data: payload })
    });

    const rawText = await response.text().catch(() => '');

    if (!response.ok) {
      throw new Error(`[${dbType}] ${tableName} ${action} 失敗：${response.status} ${rawText}`);
    }

    console.log(`✅ [SQL 寫入成功] 於資料表 ${tableName}`);
    return rawText;
  };

  const loadDatabase = async () => {
    try {
      const tables = ['GSCORP', 'GSDEPT', 'GSUSER', 'GSHOLIDAY', 'PERREC', 'MPUNCHH', 'MPUNCHD'];
      const updatedDb = { ...INITIAL_DB };

      for (const table of tables) {
        const response = await fetch(`${API_BASE_URL}/get-table?tableName=${table}&_t=${Date.now()}`, { 
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        if (!response.ok) continue;

        const rawText = await response.text();
        if (!rawText || rawText.trim() === '') continue;

        let result;
        try { result = JSON.parse(rawText); } catch { continue; }

        const rows = Array.isArray(result) ? result : (result.data || []);
        if (!rows.length) continue;

        updatedDb[table] = rows.map((row) => {
          const cleanRow = {};
          for (const key in row) {
            cleanRow[key] = typeof row[key] === 'string' ? row[key].trim() : row[key];
            if (row[key] === 1 && ['Supervisor', 'Manager', 'GM'].includes(key)) cleanRow[key] = true;
            if (row[key] === 0 && ['Supervisor', 'Manager', 'GM'].includes(key)) cleanRow[key] = false;
          }
          return cleanRow;
        });
      }

      updatedDb.PERREC = mergePerrecWithPunchStatus(updatedDb);
      setDb(updatedDb);
      console.log('✅ 成功從資料庫同步最新資料！');
    } catch (err) {
      console.error('⚠️ 無法載入最新資料', err);
    }
  };

  useEffect(() => {
    if (currentUser) loadDatabase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleUpdateDb = async (tableName, newData, action, changedItem) => {
    if (!action || changedItem == null) {
      setDb((prev) => ({ ...prev, [tableName]: newData }));
      return;
    }
    const items = Array.isArray(changedItem) ? changedItem : [changedItem];
    for (const item of items) {
      if (item == null) continue;
      await syncWithDatabase(tableName, action, item);
    }
    setDb((prev) => ({ ...prev, [tableName]: newData }));
  };

  const handleLogin = (user) => {
    setCurrentUser(user);
    setView('dashboard');
    loadDatabase();
  };

  const handleLogout = () => {
    if (isDirty) {
      const confirmLeave = window.confirm("系統警示：您有尚未送出或儲存的資料！\n確定要登出嗎？未儲存的資料將會遺失。");
      if (!confirmLeave) return;
      setIsDirty(false);
    }
    setIsDeleteMode(false); 
    setCurrentUser(null);
    setIsAdminMode(false);
    setView('login');
    sessionStorage.clear();
  };

  const handleNav = (v) => {
    if (isDirty) {
      const confirmLeave = window.confirm("系統警示：您有尚未送出或儲存的資料！\n確定要切換到其他頁面嗎？未儲存的資料將會遺失。");
      if (!confirmLeave) return;
      setIsDirty(false);
    }
    setView(v);
    setIsSidebarOpen(false);
  };

  if (view === 'login') {
    return (
      <LoginScreen
        onLogin={handleLogin}
        onAdminAccess={() => {
          setIsAdminMode(true);
          setView('admin_a');
          loadDatabase();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 flex flex-col md:flex-row overflow-hidden relative">
      
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity" 
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#2C3E50] text-white flex flex-col transition-transform duration-300 ease-in-out shadow-xl lg:shadow-none lg:static lg:translate-x-0 hide-on-print ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div 
          className="p-4 flex items-center justify-between lg:justify-start gap-3 border-b border-slate-700 cursor-pointer select-none hover:bg-slate-700/50 transition-colors"
          onDoubleClick={() => setShowDeleteAuthModal(true)}
          title="連點兩下可啟用特殊管理功能"
        >
          <div className="flex items-center gap-3 min-w-0 pointer-events-none">
            <div className="bg-blue-500 p-2 rounded-lg shrink-0">
              <Clock size={24} className="text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-[15px] whitespace-nowrap tracking-tight">AMS補打卡申請審核作業</h1>
              <p className="text-[10px] text-slate-400 truncate">Attendance Audit System</p>
            </div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); setIsSidebarOpen(false); }} className="lg:hidden p-2 text-slate-400 hover:text-white shrink-0">
            <XCircle size={24} />
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto custom-scrollbar">
          {!isAdminMode ? (
            <>
              <SidebarItem Icon={Home} label="系統首頁 (儀表板)" active={view === 'dashboard'} onClick={() => handleNav('dashboard')} />
              <div className="px-4 py-2 mt-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">各項申請</div>
              <SidebarItem Icon={Plus} label="補打卡申請" active={view === 'apply_punch'} onClick={() => handleNav('apply_punch')} />

              <div className="px-4 py-2 mt-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">稽核報表</div>
              <SidebarItem Icon={FileText} label="補打卡申請查詢" active={view === 'report1'} onClick={() => handleNav('report1')} />
              <SidebarItem Icon={BarChart2} label="補打卡紀錄統計表" active={view === 'report2'} onClick={() => handleNav('report2')} />
              
              {isManagerMode && (
                <>
                  <div className="px-4 py-2 mt-4 text-xs font-semibold text-blue-300 uppercase tracking-wider bg-slate-800/50 rounded-r-lg inline-block shadow-sm border-l-4 border-blue-400">主管審核作業</div>
                  <SidebarItem Icon={CheckCircle} label="補打卡簽核" active={view === 'approve_punch'} onClick={() => handleNav('approve_punch')} />
                </>
              )}
            </>
          ) : (
            <>
              <div className="px-4 py-2 mt-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">系統後台設定</div>
              <SidebarItem Icon={Settings} label="A. 公司基本資料" active={view === 'admin_a'} onClick={() => handleNav('admin_a')} />
              <SidebarItem Icon={Building} label="B. 部門基本資料" active={view === 'admin_b'} onClick={() => handleNav('admin_b')} />
              <SidebarItem Icon={Users} label="C. 使用者設定" active={view === 'admin_c'} onClick={() => handleNav('admin_c')} />
              <SidebarItem Icon={Calendar} label="D. 國定假日設定" active={view === 'admin_d'} onClick={() => handleNav('admin_d')} />
              <SidebarItem Icon={Upload} label="E. 打卡資料匯入(出)" active={view === 'admin_e'} onClick={() => handleNav('admin_e')} />
              <SidebarItem Icon={Key} label="F. 系統進階設定" active={view === 'admin_f'} onClick={() => handleNav('admin_f')} />
            </>
          )}
        </nav>

        <div className="p-4 border-t border-slate-700 shrink-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center font-bold">
              {activeUser?.Name?.replace('未提供姓名', '')?.charAt(0) || '管'}
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-medium truncate">
                {activeUser?.Name && activeUser?.Name !== '未提供姓名' ? activeUser.Name : '系統管理員'}
              </p>
              <p className="text-xs text-slate-400 truncate">{activeUser?.UserId || 'admin'}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
          >
            <LogOut size={16} /> 登出系統
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden bg-slate-50 relative">
        <header className="lg:hidden bg-white border-b border-slate-200 px-3 sm:px-4 py-2.5 flex items-center justify-between shrink-0 z-30 shadow-sm hide-on-print">
          <div 
            className="flex items-center gap-2 min-w-0 mr-2 cursor-pointer select-none"
            onDoubleClick={() => setShowDeleteAuthModal(true)}
          >
            <div className="bg-blue-600 p-1.5 rounded-lg text-white shrink-0 shadow-sm pointer-events-none">
              <Clock size={18} />
            </div>
            <h1 className="font-bold text-slate-800 text-base sm:text-lg truncate pointer-events-none">AMS 系統</h1>
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 py-1 pl-1 pr-2.5 sm:pr-3 rounded-full shadow-sm">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs sm:text-sm font-bold shadow-inner shrink-0">
                {activeUser?.Name?.replace('未提供姓名', '')?.charAt(0) || '管'}
              </div>
              <div className="flex flex-col justify-center max-w-[80px] sm:max-w-[120px]">
                <span className="text-[12px] sm:text-[13px] font-bold text-slate-700 leading-none mb-0.5 truncate">
                  {activeUser?.Name && activeUser?.Name !== '未提供姓名' ? activeUser.Name : '管理員'}
                </span>
                <span className="text-[9px] sm:text-[10px] text-slate-500 leading-none font-mono truncate">
                  {activeUser?.UserId || 'admin'}
                </span>
              </div>
            </div>

            <button 
              onClick={() => setIsSidebarOpen(true)} 
              className="p-1.5 -mr-1 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-slate-100 shrink-0"
              aria-label="打開選單"
            >
              <Menu size={24} />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 custom-scrollbar print-main-content relative w-full h-full">
          {view === 'dashboard' && <Dashboard currentUser={activeUser} records={db.PERREC || []} />}
          {view === 'apply_punch' && (
            <ApplyPunch 
              currentUser={activeUser} 
              db={db} 
              onUpdateDb={handleUpdateDb}
              setIsDirty={setIsDirty}
              isDeleteMode={isDeleteMode}
            />
          )}
          {view === 'report1' && (
            <ReportPunchForm 
              currentUser={activeUser} 
              db={db} 
              onUpdateDb={handleUpdateDb}
            />
          )}
          {view === 'approve_punch' && (
            <ApprovePunchForm 
              currentUser={activeUser} 
              db={db} 
              onUpdateDb={handleUpdateDb}
            />
          )}
          {view === 'report2' && (
            <ReportPunchStats 
              currentUser={activeUser} 
              db={db} 
              onUpdateDb={handleUpdateDb}
            />
          )}

          {view === 'admin_a' && <AdminCorp db={db} onUpdate={handleUpdateDb} />}
          {view === 'admin_b' && <AdminDept db={db} onUpdate={handleUpdateDb} />}
          {view === 'admin_c' && <AdminUser db={db} onUpdate={handleUpdateDb} />}
          {view === 'admin_d' && <AdminHoliday db={db} onUpdate={handleUpdateDb} />}
          {view === 'admin_e' && <AdminImport db={db} setIsDirty={setIsDirty} onImportSuccess={loadDatabase} />}
          {view === 'admin_f' && <AdminSettings />}
        </main>
      </div>

      {showDeleteAuthModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-sm animate-fade-in">
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2 border-b pb-4">
              <Key size={24} className="text-rose-600" /> 啟用特殊刪除模式
            </h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const currentAdminPwd = localStorage.getItem('ams_admin_pwd') || 'admin';
              if (deleteAuthPwd === currentAdminPwd) {
                setIsDeleteMode(true);
                setShowDeleteAuthModal(false);
                setDeleteAuthPwd('');
                alert('✅ 刪除模式已啟用！\n現在您可以在「補打卡申請」清單中直接剔除異常紀錄。');
              } else {
                alert('密碼錯誤');
              }
            }}>
              <input
                type="password"
                autoFocus
                value={deleteAuthPwd}
                onChange={(e) => setDeleteAuthPwd(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-6 focus:ring-2 focus:ring-rose-500 outline-none"
                placeholder="請輸入後台密碼"
              />
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                <button type="button" onClick={() => { setShowDeleteAuthModal(false); setDeleteAuthPwd(''); }} className="w-full sm:w-auto px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg font-medium">
                  取消
                </button>
                <button type="submit" className="w-full sm:w-auto px-5 py-2.5 bg-rose-600 text-white rounded-lg shadow hover:bg-rose-700 font-medium">
                  驗證並啟用
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
            .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

            @media print {
              @page {
                size: A4 portrait;
                margin: 0;
              }
              body {
                background-color: white !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
                margin: 0 !important;
                padding: 0 !important;
              }
              body * { visibility: visible !important; }
              aside, header { display: none !important; }
              .hide-on-print { display: none !important; }
              .print-hide-col { display: none !important; }
              .print-main-content {
                padding: 0 !important;
                overflow: visible !important;
                height: auto !important;
                background: white !important;
              }
              .print-container {
                position: static !important;
                width: 100%;
                max-width: 100%;
                margin: 0 !important;
                padding: 0 !important;
                box-shadow: none !important;
                border: none !important;
              }
              .print-no-border {
                border: none !important;
                box-shadow: none !important;
                padding: 0 !important;
                margin: 0 !important;
              }
              .print-overflow-visible {
                overflow: visible !important;
              }
              /* 每個 chunk = 一頁，用 transform 縮放到 A4 */
              .break-after-page {
                page-break-after: always !important;
                break-after: page !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                display: block !important;
                width: 210mm !important;
                height: 297mm !important;
                overflow: hidden !important;
                margin: 0 !important;
                padding: 8mm !important;
                box-sizing: border-box !important;
              }
              .break-after-page:last-child {
                page-break-after: auto !important;
                break-after: auto !important;
              }
              .break-after-page table {
                width: 100% !important;
                table-layout: fixed !important;
                border-collapse: collapse !important;
                font-size: 13px !important;
                line-height: 1.6 !important;
              }
              .break-after-page td,
              .break-after-page th {
                padding: 3px 3px !important;
                font-size: 13px !important;
              }
              .break-after-page h2 {
                font-size: 20px !important;
                margin-bottom: 4px !important;
                margin-top: 0 !important;
              }
              .break-after-page .flex.justify-between {
                font-size: 13px !important;
                margin-bottom: 4px !important;
              }
            }
          `
        }}
      />
    </div>
  );
}

// ==========================================
// 8. Shared Components definitions
// ==========================================
function SidebarItem({ Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
        active
          ? 'bg-blue-600 text-white border-l-4 border-blue-400'
          : 'text-slate-300 hover:bg-slate-800 hover:text-white border-l-4 border-transparent'
      }`}
    >
      <Icon size={18} />
      {label}
    </button>
  );
}

function AdminSection({ title, Icon, onAdd, extraActions, children }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 sm:p-6 flex flex-col max-h-[85vh] lg:max-h-full">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 shrink-0">
        <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2">
          <Icon size={22} className="text-slate-500" /> {title}
        </h2>
        <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
          {extraActions}
          {onAdd && (
            <button
              onClick={onAdd}
              className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2.5 sm:py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center justify-center gap-2 shadow-sm transition-colors"
            >
              <Plus size={16} /> 新增資料
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto w-full custom-scrollbar flex-1 -mx-4 sm:mx-0 px-4 sm:px-0">
        {children}
      </div>
    </div>
  );
}

function EditModal({ title, onClose, onSave, children }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-3 sm:p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col animate-fade-in">
        <div className="bg-slate-800 px-5 sm:px-6 py-4 flex justify-between items-center text-white shrink-0">
          <h3 className="font-bold text-base sm:text-lg">{title}</h3>
          <button onClick={onClose} className="hover:text-slate-300 transition-colors p-1 -mr-1">
            <XCircle size={22} />
          </button>
        </div>
        <div className="p-5 sm:p-6 space-y-4 overflow-y-auto custom-scrollbar flex-1">
          {children}
        </div>
        <div className="bg-slate-50 px-5 sm:px-6 py-4 flex flex-col-reverse sm:flex-row justify-end gap-3 border-t shrink-0">
          <button className="w-full sm:w-auto px-5 py-2.5 sm:py-2 text-slate-600 hover:bg-slate-200 border border-slate-300 sm:border-transparent rounded-lg font-medium transition-colors" onClick={onClose}>
            取消
          </button>
          <button className="w-full sm:w-auto px-5 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 font-medium transition-colors" onClick={onSave}>
            儲存設定
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminCorp({ db, onUpdate }) {
  const data = db.GSCORP || [];
  const [modal, setModal] = useState({ open: false, index: -1, formData: {} });

  const handleSave = async () => {
    if (!modal.formData.Code || !modal.formData.Name) {
      alert('請填寫完整公司資料');
      return;
    }
    try {
      if (modal.index >= 0) {
        const newData = [...data];
        newData[modal.index] = modal.formData;
        await onUpdate('GSCORP', newData, 'UPDATE', modal.formData);
      } else {
        const newSno = data.length > 0 ? Math.max(...data.map((d) => d.Sno)) + 1 : 1;
        const newItem = { ...modal.formData, Sno: newSno };
        await onUpdate('GSCORP', [...data, newItem], 'INSERT', newItem);
      }
      setModal({ open: false, index: -1, formData: {} });
      alert('✅ 公司資料儲存成功！');
    } catch (err) {
      alert('⚠️ 儲存失敗：' + err.message);
    }
  };

  const handleDelete = async (index) => {
    if (window.confirm('確定要刪除這筆公司資料嗎？')) {
      const itemToDelete = data[index];
      try {
        await onUpdate('GSCORP', data.filter((_, i) => i !== index), 'DELETE', itemToDelete);
        alert('✅ 刪除成功！');
      } catch (err) {
        alert('⚠️ 刪除失敗：' + err.message);
      }
    }
  };

  return (
    <AdminSection title="A. 公司基本資料" Icon={Settings} onAdd={() => setModal({ open: true, index: -1, formData: { Code: '', Name: '' } })}>
      <table className="w-full text-left border-collapse min-w-[400px]">
        <thead>
          <tr className="bg-slate-100 text-slate-600 text-sm border-y border-slate-200">
            <th className="py-3 px-4 font-semibold whitespace-nowrap">公司代號</th>
            <th className="py-3 px-4 font-semibold whitespace-nowrap">公司名稱</th>
            <th className="py-3 px-4 font-semibold w-24 text-center whitespace-nowrap">操作</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr><td colSpan="3" className="py-8 text-center text-slate-500">尚無資料</td></tr>
          )}
          {data.map((row, i) => (
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 text-sm">
              <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">{row.Code}</td>
              <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{row.Name}</td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <button onClick={() => setModal({ open: true, index: i, formData: row })} className="p-1.5 text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded mr-2 transition-colors">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(i)} className="p-1.5 text-rose-500 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 rounded transition-colors">
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {modal.open && (
        <EditModal title={modal.index >= 0 ? '編輯公司資料' : '新增公司資料'} onClose={() => setModal({ open: false, index: -1, formData: {} })} onSave={handleSave}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">公司代號 (Code)</label>
              <input
                className={`w-full border rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${modal.index >= 0 ? 'bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200' : 'border-slate-300'}`}
                value={modal.formData.Code || ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Code: e.target.value } })}
                disabled={modal.index >= 0}
              />
              {modal.index >= 0 && <p className="text-xs text-rose-500 mt-1">主鍵欄位不可修改</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">公司名稱 (Name)</label>
              <input
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={modal.formData.Name || ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Name: e.target.value } })}
              />
            </div>
          </div>
        </EditModal>
      )}
    </AdminSection>
  );
}

function AdminDept({ db, onUpdate }) {
  const data = db.GSDEPT || [];
  const [modal, setModal] = useState({ open: false, index: -1, formData: {} });

  const handleSave = async () => {
    if (!modal.formData.CorpSno || !modal.formData.Code || !modal.formData.Name) {
      alert('請填寫完整部門資料');
      return;
    }
    try {
      if (modal.index >= 0) {
        const newData = [...data];
        newData[modal.index] = modal.formData;
        await onUpdate('GSDEPT', newData, 'UPDATE', modal.formData);
      } else {
        await onUpdate('GSDEPT', [...data, modal.formData], 'INSERT', modal.formData);
      }
      setModal({ open: false, index: -1, formData: {} });
      alert('✅ 部門資料儲存成功！');
    } catch (err) {
      alert('⚠️ 儲存失敗：' + err.message);
    }
  };

  const handleDelete = async (index) => {
    if (window.confirm('確定要刪除這筆部門資料嗎？')) {
      const itemToDelete = data[index];
      try {
        await onUpdate('GSDEPT', data.filter((_, i) => i !== index), 'DELETE', itemToDelete);
        alert('✅ 刪除成功！');
      } catch (err) {
        alert('⚠️ 刪除失敗：' + err.message);
      }
    }
  };

  return (
    <AdminSection title="B. 部門基本資料" Icon={Building} onAdd={() => setModal({ open: true, index: -1, formData: { CorpSno: '', Code: '', Name: '' } })}>
      <table className="w-full text-left border-collapse min-w-[500px]">
        <thead>
          <tr className="bg-slate-100 text-slate-600 text-sm border-y border-slate-200">
            <th className="py-3 px-4 font-semibold whitespace-nowrap">所屬公司</th>
            <th className="py-3 px-4 font-semibold whitespace-nowrap">部門代號</th>
            <th className="py-3 px-4 font-semibold whitespace-nowrap">部門名稱</th>
            <th className="py-3 px-4 font-semibold w-24 text-center whitespace-nowrap">操作</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr><td colSpan="4" className="py-8 text-center text-slate-500">尚無資料</td></tr>
          )}
          {data.map((row, i) => {
            const corpName = db.GSCORP?.find((c) => c.Sno === row.CorpSno)?.Name || row.CorpSno;
            return (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 text-sm">
                <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{corpName}</td>
                <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">{row.Code}</td>
                <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{row.Name}</td>
                <td className="py-3 px-4 text-center whitespace-nowrap">
                  <button onClick={() => setModal({ open: true, index: i, formData: row })} className="p-1.5 text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded mr-2 transition-colors">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDelete(i)} className="p-1.5 text-rose-500 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 rounded transition-colors">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {modal.open && (
        <EditModal title={modal.index >= 0 ? '編輯部門資料' : '新增部門資料'} onClose={() => setModal({ open: false, index: -1, formData: {} })} onSave={handleSave}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">所屬公司</label>
              <select
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                value={modal.formData.CorpSno || ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, CorpSno: Number(e.target.value) } })}
              >
                <option value="">請選擇所屬公司</option>
                {db.GSCORP?.map((c) => (
                  <option key={c.Sno} value={c.Sno}>{c.Name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">部門代號 (Code)</label>
                <input
                  className={`w-full border rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${modal.index >= 0 ? 'bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200' : 'border-slate-300'}`}
                  value={modal.formData.Code || ''}
                  onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Code: e.target.value } })}
                  disabled={modal.index >= 0}
                />
                {modal.index >= 0 && <p className="text-[11px] text-rose-500 mt-1">主鍵不可修改</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">部門名稱 (Name)</label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={modal.formData.Name || ''}
                  onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Name: e.target.value } })}
                />
              </div>
            </div>
          </div>
        </EditModal>
      )}
    </AdminSection>
  );
}

function AdminUser({ db, onUpdate }) {
  const data = db.GSUSER || [];
  const [modal, setModal] = useState({ open: false, index: -1, formData: {} });
  
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [broadcastMsg, setBroadcastMsg] = useState('【系統推播通知】\n本月份出勤資料已上傳至「企業差勤系統」囉！\n\n煩請各位同仁登入系統查看您的打卡紀錄，\n若有【未打卡】或異常的部分，請盡快提出「補打卡申請」，以利單位主管能及早進行審核作業。\n謝謝您的配合！');
  const [isSending, setIsSending] = useState(false);

  const handleSave = async () => {
    if (!modal.formData.UserId || !modal.formData.Name || !modal.formData.Passwd) {
      alert('請填寫完整使用者資料');
      return;
    }
    const payload = {
      ...modal.formData,
      Supervisor: modal.formData.Supervisor ? 1 : 0,
      Manager: modal.formData.Manager ? 1 : 0,
      GM: modal.formData.GM ? 1 : 0
    };
    try {
      if (modal.index >= 0) {
        const newData = [...data];
        newData[modal.index] = modal.formData;
        await onUpdate('GSUSER', newData, 'UPDATE', payload);
      } else {
        await onUpdate('GSUSER', [...data, modal.formData], 'INSERT', payload);
      }
      setModal({ open: false, index: -1, formData: {} });
      alert('✅ 使用者資料儲存成功！');
    } catch (err) {
      alert('⚠️ 儲存失敗：' + err.message);
    }
  };

  const handleDelete = async (index) => {
    if (window.confirm('確定要刪除此使用者嗎？')) {
      const itemToDelete = data[index];
      try {
        await onUpdate('GSUSER', data.filter((_, i) => i !== index), 'DELETE', itemToDelete);
        alert('✅ 刪除成功！');
      } catch (err) {
        alert('⚠️ 刪除失敗：' + err.message);
      }
    }
  };

  const handleSelectAllGeneral = () => {
    const generalStaffIds = data
      .filter(u => !u.Supervisor && !u.Manager && !u.GM)
      .map(u => u.UserId);
    setSelectedTargets(generalStaffIds);
  };

  const handleSelectAll = () => {
    setSelectedTargets(data.map(u => u.UserId));
  };

  const handleToggleTarget = (userId) => {
    setSelectedTargets(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSendBroadcast = async () => {
    if (selectedTargets.length === 0) {
      alert('請至少選擇一位推播對象！');
      return;
    }
    if (!broadcastMsg.trim()) {
      alert('請填寫推播訊息內容！');
      return;
    }

    setIsSending(true);
    try {
      const endpoint = `${API_BASE_URL}/line-broadcast`;
      
      const payload = {
        users: selectedTargets,
        message: broadcastMsg
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        throw new Error(`伺服器回傳錯誤代碼: ${res.status}`);
      }

      alert(`✅ 成功向 ${selectedTargets.length} 位同仁發送 LINE 推播通知！`);
      setShowBroadcast(false);
      setSelectedTargets([]);
    } catch (error) {
      alert(`⚠️ 發送推播失敗：${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const LineBroadcastButton = (
    <button
      onClick={() => setShowBroadcast(true)}
      className="w-full sm:w-auto bg-[#06C755] text-white px-4 py-2.5 sm:py-2 rounded-lg text-sm hover:bg-[#05b04b] flex items-center justify-center gap-2 shadow-sm transition-colors"
    >
      <MessageCircle size={16} /> LINE 群發通知
    </button>
  );

  return (
    <AdminSection
      title="C. 使用者設定"
      Icon={Users}
      extraActions={LineBroadcastButton}
      onAdd={() => setModal({ open: true, index: -1, formData: { UserId: '', Name: '', Passwd: '', Corpcode: '', Deptcode: '', Supervisor: false, Manager: false, GM: false } })}
    >
      <table className="w-full text-left border-collapse min-w-[900px]">
        <thead>
          <tr className="bg-slate-100 text-slate-600 text-sm border-y border-slate-200">
            <th className="py-3 px-4 font-semibold whitespace-nowrap">員工代號</th>
            <th className="py-3 px-4 font-semibold whitespace-nowrap">姓名</th>
            <th className="py-3 px-4 font-semibold whitespace-nowrap">密碼</th>
            <th className="py-3 px-4 font-semibold whitespace-nowrap">所屬公司</th>
            <th className="py-3 px-4 font-semibold whitespace-nowrap">所屬部門</th>
            <th className="py-3 px-2 font-semibold text-center whitespace-nowrap">單位主管</th>
            <th className="py-3 px-2 font-semibold text-center whitespace-nowrap">部門主管</th>
            <th className="py-3 px-2 font-semibold text-center whitespace-nowrap">總經理</th>
            <th className="py-3 px-4 font-semibold w-24 text-center whitespace-nowrap">操作</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr><td colSpan="9" className="py-8 text-center text-slate-500">尚無資料</td></tr>
          )}
          {data.map((row, i) => {
            const corpName = db.GSCORP?.find((c) => c.Code === row.Corpcode)?.Name || row.Corpcode;
            const deptName = db.GSDEPT?.find((d) => d.Code === row.Deptcode)?.Name || row.Deptcode;
            return (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 text-sm">
                <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">{row.UserId}</td>
                <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{row.Name}</td>
                <td className="py-3 px-4 text-slate-400 whitespace-nowrap">****</td>
                <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{corpName}</td>
                <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{deptName}</td>
                <td className="py-3 px-2 text-center">
                  {row.Supervisor ? <CheckCircle size={16} className="text-green-600 inline" /> : <span className="text-slate-300">-</span>}
                </td>
                <td className="py-3 px-2 text-center">
                  {row.Manager ? <CheckCircle size={16} className="text-green-600 inline" /> : <span className="text-slate-300">-</span>}
                </td>
                <td className="py-3 px-2 text-center">
                  {row.GM ? <CheckCircle size={16} className="text-green-600 inline" /> : <span className="text-slate-300">-</span>}
                </td>
                <td className="py-3 px-4 text-center whitespace-nowrap">
                  <button onClick={() => setModal({ open: true, index: i, formData: row })} className="p-1.5 text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded mr-2 transition-colors">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDelete(i)} className="p-1.5 text-rose-500 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 rounded transition-colors">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {modal.open && (
        <EditModal title={modal.index >= 0 ? '編輯使用者資料' : '新增使用者'} onClose={() => setModal({ open: false, index: -1, formData: {} })} onSave={handleSave}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">員工代號</label>
              <input
                className={`w-full border rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${modal.index >= 0 ? 'bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200' : 'border-slate-300'}`}
                value={modal.formData.UserId || ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, UserId: e.target.value } })}
                disabled={modal.index >= 0}
              />
              {modal.index >= 0 && <p className="text-[11px] text-rose-500 mt-1">主鍵不可修改</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">姓名</label>
              <input
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={modal.formData.Name || ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Name: e.target.value } })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">密碼</label>
              <input
                type="text"
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={modal.formData.Passwd || ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Passwd: e.target.value } })}
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">所屬公司</label>
              <select
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                value={modal.formData.Corpcode || ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Corpcode: e.target.value } })}
              >
                <option value="">請選擇</option>
                {db.GSCORP?.map((c) => (
                  <option key={c.Code} value={c.Code}>{c.Name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">所屬部門</label>
              <select
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                value={modal.formData.Deptcode || ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Deptcode: e.target.value } })}
              >
                <option value="">請選擇</option>
                {db.GSDEPT?.map((d) => (
                  <option key={d.Code} value={d.Code}>{d.Name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <p className="text-sm font-semibold text-slate-700 mb-3 border-b pb-2">
              管理層級 <span className="text-[11px] font-normal text-slate-500">(勾選代表具備該主管身分)</span>
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-5 h-5 sm:w-4 sm:h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                  checked={modal.formData.Supervisor || false}
                  onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Supervisor: e.target.checked } })}
                />
                <span className="text-sm">單位主管</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-5 h-5 sm:w-4 sm:h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                  checked={modal.formData.Manager || false}
                  onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Manager: e.target.checked } })}
                />
                <span className="text-sm">部門主管</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-5 h-5 sm:w-4 sm:h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                  checked={modal.formData.GM || false}
                  onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, GM: e.target.checked } })}
                />
                <span className="text-sm">總經理</span>
              </label>
            </div>
          </div>
        </EditModal>
      )}

      {showBroadcast && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-3 sm:p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full sm:max-w-3xl max-h-[90vh] flex flex-col animate-fade-in overflow-hidden">
            <div className="bg-[#06C755] px-5 sm:px-6 py-4 flex justify-between items-center text-white shrink-0">
              <h3 className="font-bold text-base sm:text-lg flex items-center gap-2">
                <MessageCircle size={22} /> LINE 推播群發通知
              </h3>
              <button onClick={() => setShowBroadcast(false)} className="hover:text-white/80 transition-colors p-1 -mr-1">
                <XCircle size={22} />
              </button>
            </div>
            
            <div className="p-5 sm:p-6 flex flex-col gap-6 overflow-y-auto custom-scrollbar">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">推播文字內容</label>
                <textarea 
                  className="w-full h-32 p-3 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#06C755] focus:border-[#06C755] transition-all resize-none text-sm"
                  value={broadcastMsg}
                  onChange={(e) => setBroadcastMsg(e.target.value)}
                  placeholder="請輸入推播訊息內容..."
                />
              </div>

              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-bold text-slate-700">
                    勾選推播對象 <span className="font-normal text-slate-500 text-xs ml-2">已選擇 {selectedTargets.length} 人</span>
                  </label>
                  <div className="flex gap-2">
                    <button onClick={handleSelectAllGeneral} className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded border border-blue-200 transition-colors">
                      一鍵全選一般員工
                    </button>
                    <button onClick={handleSelectAll} className="text-xs px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded border border-slate-200 transition-colors">
                      全選所有人
                    </button>
                    <button onClick={() => setSelectedTargets([])} className="text-xs px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded border border-slate-200 transition-colors">
                      清除選擇
                    </button>
                  </div>
                </div>
                
                <div className="border border-slate-200 rounded-lg overflow-hidden flex flex-col max-h-[300px]">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500">
                    <div className="col-span-1 text-center">選取</div>
                    <div className="col-span-3">員工代號</div>
                    <div className="col-span-3">姓名</div>
                    <div className="col-span-3">所屬部門</div>
                    <div className="col-span-2 text-center">身分</div>
                  </div>
                  <div className="overflow-y-auto custom-scrollbar flex-1 bg-white">
                    {data.map((user) => {
                      const isSelected = selectedTargets.includes(user.UserId);
                      const isManager = user.Supervisor || user.Manager || user.GM;
                      const deptName = db.GSDEPT?.find(d => d.Code === user.Deptcode)?.Name || user.Deptcode;
                      
                      return (
                        <label 
                          key={user.UserId} 
                          className={`px-4 py-2.5 border-b border-slate-100 grid grid-cols-12 gap-2 items-center cursor-pointer transition-colors ${isSelected ? 'bg-green-50/50' : 'hover:bg-slate-50'}`}
                        >
                          <div className="col-span-1 flex justify-center text-slate-400">
                            <input 
                              type="checkbox" 
                              className="sr-only" 
                              checked={isSelected}
                              onChange={() => handleToggleTarget(user.UserId)}
                            />
                            {isSelected ? (
                              <CheckSquare className="text-[#06C755]" size={18} />
                            ) : (
                              <Square size={18} />
                            )}
                          </div>
                          <div className="col-span-3 text-sm text-slate-700 font-medium">{user.UserId}</div>
                          <div className="col-span-3 text-sm text-slate-700">{user.Name}</div>
                          <div className="col-span-3 text-sm text-slate-500 truncate">{deptName}</div>
                          <div className="col-span-2 text-center">
                            {isManager ? (
                              <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">主管</span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">一般</span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 px-5 sm:px-6 py-4 flex flex-col-reverse sm:flex-row justify-end gap-3 border-t shrink-0">
              <button 
                className="w-full sm:w-auto px-5 py-2.5 sm:py-2 text-slate-600 hover:bg-slate-200 border border-slate-300 sm:border-transparent rounded-lg font-medium transition-colors disabled:opacity-50" 
                onClick={() => setShowBroadcast(false)}
                disabled={isSending}
              >
                取消
              </button>
              <button 
                className="w-full sm:w-auto px-5 py-2.5 sm:py-2 bg-[#06C755] text-white rounded-lg shadow hover:bg-[#05b04b] font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                onClick={handleSendBroadcast}
                disabled={isSending}
              >
                {isSending ? (
                  <>處理中...</>
                ) : (
                  <><Send size={16} /> 立即發送推播</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminSection>
  );
}

function AdminHoliday({ db, onUpdate }) {
  const data = db.GSHOLIDAY || [];
  const [modal, setModal] = useState({ open: false, index: -1, formData: {} });

  const handleSave = async () => {
    if (!modal.formData.Date || !modal.formData.Name) {
      alert('請填寫完整假日資料');
      return;
    }
    try {
      if (modal.index >= 0) {
        const newData = [...data];
        newData[modal.index] = modal.formData;
        await onUpdate('GSHOLIDAY', newData, 'UPDATE', modal.formData);
      } else {
        const newSno = data.length > 0 ? Math.max(...data.map((d) => d.Sno)) + 1 : 1;
        const newItem = { ...modal.formData, Sno: newSno };
        await onUpdate('GSHOLIDAY', [...data, newItem], 'INSERT', newItem);
      }
      setModal({ open: false, index: -1, formData: {} });
      alert('✅ 假日資料儲存成功！');
    } catch (err) {
      alert('⚠️ 儲存失敗：' + err.message);
    }
  };

  const handleDelete = async (index) => {
    if (window.confirm('確定要刪除這筆國定假日嗎？')) {
      const itemToDelete = data[index];
      try {
        await onUpdate('GSHOLIDAY', data.filter((_, i) => i !== index), 'DELETE', itemToDelete);
        alert('✅ 刪除成功！');
      } catch (err) {
        alert('⚠️ 刪除失敗：' + err.message);
      }
    }
  };

  return (
    <AdminSection title="D. 國定假日設定" Icon={Calendar} onAdd={() => setModal({ open: true, index: -1, formData: { Date: '', Name: '' } })}>
      <table className="w-full text-left border-collapse min-w-[400px]">
        <thead>
          <tr className="bg-slate-100 text-slate-600 text-sm border-y border-slate-200">
            <th className="py-3 px-4 font-semibold whitespace-nowrap">日期</th>
            <th className="py-3 px-4 font-semibold whitespace-nowrap">假期名稱</th>
            <th className="py-3 px-4 font-semibold w-24 text-center whitespace-nowrap">操作</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr><td colSpan="3" className="py-8 text-center text-slate-500">尚無資料</td></tr>
          )}
          {data.map((row, i) => (
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 text-sm">
              <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">{row.Date ? String(row.Date).split('T')[0] : ''}</td>
              <td className="py-3 px-4 text-slate-700 whitespace-nowrap">{row.Name}</td>
              <td className="py-3 px-4 text-center whitespace-nowrap">
                <button onClick={() => setModal({ open: true, index: i, formData: row })} className="p-1.5 text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded mr-2 transition-colors">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(i)} className="p-1.5 text-rose-500 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 rounded transition-colors">
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {modal.open && (
        <EditModal title={modal.index >= 0 ? '編輯國定假日' : '新增國定假日'} onClose={() => setModal({ open: false, index: -1, formData: {} })} onSave={handleSave}>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">日期 (YYYY-MM-DD)</label>
              <input
                type="date"
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={modal.formData.Date ? String(modal.formData.Date).split('T')[0] : ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Date: e.target.value } })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">假期名稱</label>
              <input
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={modal.formData.Name || ''}
                onChange={(e) => setModal({ ...modal, formData: { ...modal.formData, Name: e.target.value } })}
              />
            </div>
          </div>
        </EditModal>
      )}
    </AdminSection>
  );
}

function AdminSettings() {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const handleSave = () => {
    const currentAdminPwd = localStorage.getItem('ams_admin_pwd') || 'admin';
    if (oldPwd !== currentAdminPwd) {
      alert('⚠️ 原密碼輸入錯誤，請重新輸入！');
      return;
    }
    if (!newPwd) {
      alert('⚠️ 新密碼不可為空！');
      return;
    }
    if (newPwd !== confirmPwd) {
      alert('⚠️ 兩次輸入的新密碼不一致！');
      return;
    }

    localStorage.setItem('ams_admin_pwd', newPwd);
    alert('✅ 後台管理員密碼修改成功！');
    setOldPwd('');
    setNewPwd('');
    setConfirmPwd('');
  };

  return (
    <AdminSection title="F. 系統進階設定" Icon={Key}>
      <div className="max-w-md w-full bg-white p-6 rounded-xl border border-slate-200 shadow-sm mt-4">
        <h3 className="text-base font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
          <Settings size={18} className="text-blue-500" /> 修改後台登入密碼
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">原密碼</label>
            <input
              type="password"
              className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="請輸入原後台密碼"
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">新密碼</label>
            <input
              type="password"
              className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="請輸入新的密碼"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">確認新密碼</label>
            <input
              type="password"
              className="w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="請再次輸入新密碼"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
            />
          </div>
          <div className="pt-4 border-t border-slate-100 flex justify-end">
            <button
              onClick={handleSave}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm w-full sm:w-auto"
            >
              儲存密碼設定
            </button>
          </div>
        </div>
      </div>
    </AdminSection>
  );
}

function LoginScreen({ onLogin, onAdminAccess }) {
  const [userid, setUserid] = useState('');
  const [password, setPassword] = useState('');
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminPwd, setAdminPwd] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const pressTimer = React.useRef(null);

  const handleTouchStart = () => {
    pressTimer.current = setTimeout(() => {
      setShowAdminModal(true);
    }, 1200); 
  };

  const handleTouchEnd = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
  };

  const [lineProfile, setLineProfile] = useState(null);
  const LIFF_ID = "2009756912-7Gv0nVWu"; 

  useEffect(() => {
    if (!LIFF_ID || LIFF_ID.includes('請在這裡填入')) return;

    const script = document.createElement('script');
    script.src = "https://static.line-scdn.net/liff/edge/2/sdk.js";
    script.async = true;
    script.onload = () => {
      if (window.liff) {
        window.liff.init({ liffId: LIFF_ID })
          .then(() => {
            if (window.liff.isLoggedIn()) {
              window.liff.getProfile().then(profile => {
                console.log("✅ 成功抓取 LINE Profile:", profile);
                setLineProfile(profile);
              }).catch(err => console.error("⚠️ 取得 Profile 失敗", err));
            }
          })
          .catch(err => {
            console.error("⚠️ LIFF 初始化失敗", err);
          });
      }
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userid || !password) {
      setError('請輸入員工編號與密碼');
      return;
    }

    setIsLoggingIn(true);
    setError('');

    try {
      const payload = { 
        userid, 
        password,
        lineUserId: lineProfile ? lineProfile.userId : '' 
      };

      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`伺服器回應代碼: ${response.status}`);
      const rawText = await response.text();
      let result;
      try { result = JSON.parse(rawText); } catch { throw new Error('JSON_PARSE_ERROR'); }

      if (result.success) {
        const u = result.user || {};
        const cleanUser = {
          ...u,
          UserId: String(pick(u, ['UserId', 'Code'], userid)).trim(),
          Name: String(pick(u, ['Name'], '未提供姓名')).trim(),
          Deptcode: String(pick(u, ['Deptcode'])).trim(),
          Corpcode: String(pick(u, ['Corpcode'])).trim()
        };
        onLogin(cleanUser);
      } else {
        setError(result.message || '登入失敗');
      }
    } catch (err) {
      if (err.message === 'JSON_PARSE_ERROR') setError('資料解析失敗：請檢查 n8n 回傳。');
      else if (err.message.includes('Failed to fetch')) setError('連線被拒：請確認 n8n 工作流已 Active。');
      else setError('系統錯誤：' + err.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleAdminVerify = (e) => {
    e.preventDefault();
    const currentAdminPwd = localStorage.getItem('ams_admin_pwd') || 'admin';
    if (adminPwd === currentAdminPwd) {
      setShowAdminModal(false);
      setAdminPwd('');
      onAdminAccess();
    } else {
      alert('密碼錯誤');
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#f0f4f8] p-4">
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden select-none"
        style={{ WebkitTouchCallout: 'none' }}
        onContextMenu={(e) => { e.preventDefault(); setShowAdminModal(true); }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchEnd}
      >
        <div className="bg-blue-600 p-8 text-center text-white pointer-events-none">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Clock size={32} className="text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold">AMS補打卡申請審核作業</h2>
          <p className="text-blue-200 text-sm mt-1">Attendance & Audit Portal</p>
        </div>

        <div className="p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {lineProfile && (
              <div className="bg-[#e5f9ed] border border-[#06C755]/30 text-[#05b04b] px-4 py-3 rounded-lg text-sm flex items-start gap-3 mb-2 animate-fade-in shadow-sm">
                <CheckCircle size={18} className="mt-0.5 shrink-0" />
                <div>
                  <strong className="block mb-0.5 text-slate-800">已連結您的 LINE 帳號：{lineProfile.displayName}</strong>
                  完成登入後，系統將自動為您綁定「出勤通知」推播服務。
                </div>
              </div>
            )}

            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100">{error}</div>}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">員工編號 (UserId)</label>
              <input
                type="text"
                value={userid}
                onChange={(e) => setUserid(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="請輸入員工編號"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">密碼 (Password)</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                placeholder="請輸入密碼"
              />
            </div>

            <button 
              type="submit" 
              disabled={isLoggingIn}
              className={`w-full text-white font-bold py-3 rounded-lg transition shadow-md flex items-center justify-center gap-2 ${
                isLoggingIn ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              {isLoggingIn ? '登入中...' : '登入系統'}
            </button>

            <p className="text-xs text-center text-slate-400 mt-6">提示: 在此視窗點擊「滑鼠右鍵」或「長按 1 秒」可呼叫管理員後台</p>
          </form>
        </div>
      </div>

      {showAdminModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-sm animate-fade-in">
            <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2 border-b pb-4">
              <Settings size={24} className="text-blue-600" /> 管理員驗證
            </h3>
            <form onSubmit={handleAdminVerify}>
              <input
                type="password"
                autoFocus
                value={adminPwd}
                onChange={(e) => setAdminPwd(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-6 focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="請輸入後台密碼"
              />
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                <button type="button" onClick={() => setShowAdminModal(false)} className="w-full sm:w-auto px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg font-medium">
                  取消
                </button>
                <button type="submit" className="w-full sm:w-auto px-5 py-2.5 bg-slate-800 text-white rounded-lg shadow hover:bg-slate-900 font-medium">
                  進入後台
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ currentUser, records = [] }) {
  const [selectedMonth, setSelectedMonth] = useState('');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const str = String(dateStr);
    if (str.includes('T')) return str.split('T')[0];
    return str.substring(0, 10);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '------';
    const str = String(timeStr);
    if (str.includes('T')) {
      const timePart = str.split('T')[1];
      if (timePart) return timePart.substring(0, 8);
    }
    if (str.length >= 8 && str.includes(':')) {
      const match = str.match(/\d{2}:\d{2}:\d{2}/);
      if (match) return match[0];
    }
    return str || '------';
  };

  const allEnrichedRecords = records
    .filter(r => String(pick(r, ['Code'], '')).trim() === currentUser?.UserId)
    .sort((a, b) => new Date(pick(a, ['Sdate'], '')) - new Date(pick(b, ['Sdate'], '')))
    .map(row => {
      const expectedStart = formatTime(getExpectedIn(row));
      const expectedEnd = formatTime(getExpectedOut(row));
      const actualStart = formatTime(getActualIn(row));
      const actualEnd = formatTime(getActualOut(row));
      let status = buildCombinedAbnormalStatus(row, String(pick(row, ['Rcode'], '') || '').trim());

      status = status.replace(/,+/g, ',').replace(/^,|,$/g, '').trim();

      return {
        ...row,
        status,
        displayDate: formatDate(pick(row, ['Sdate'], '')),
        displayRSTimestart: expectedStart || '------',
        displayRSTimeend: expectedEnd || '------',
        displayASTimestart: actualStart || '------',
        displayASTimeend: actualEnd || '------',
      };
    });

  const availableMonths = [...new Set(allEnrichedRecords.map(r => r.displayDate.substring(0, 7)))].sort((a, b) => b.localeCompare(a));

  useEffect(() => {
    if (!selectedMonth) {
      const current = new Date().toISOString().substring(0, 7);
      if (availableMonths.includes(current)) setSelectedMonth(current);
      else if (availableMonths.length > 0) setSelectedMonth(availableMonths[0]);
    }
  }, [availableMonths, selectedMonth]);

  const displayRecords = allEnrichedRecords.filter(r => r.displayDate.startsWith(selectedMonth));
  const missedPunches = displayRecords.filter(r => String(r.status || '').includes('未打卡')).length;
  const totalPunches = displayRecords.length;
  const displayTitleMonth = selectedMonth ? selectedMonth.replace('-', '/') : '';

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in w-full">
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
          <BarChart2 className="text-blue-600" /> 系統稽核總覽
        </h2>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 overflow-hidden">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <AlertTriangle className="text-rose-500" size={20} />
            <span className="text-red-600">{displayTitleMonth}</span> 打卡異常稽核
          </h3>

          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className="border border-slate-300 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 font-medium w-full sm:w-auto"
          >
            {selectedMonth && <option value={selectedMonth}>{selectedMonth}</option>}
            {availableMonths.map(m => m !== selectedMonth && <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
          <div className="bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-100 flex flex-col justify-center">
            <div className="text-slate-500 text-xs sm:text-sm mb-1">打卡資料總數</div>
            <div className="text-2xl sm:text-3xl font-bold text-slate-700">
              {totalPunches}
              <span className="text-sm sm:text-lg ml-1 text-slate-500 font-normal">筆</span>
            </div>
          </div>

          <div className="bg-rose-50 p-3 sm:p-4 rounded-lg border border-rose-100 relative overflow-hidden flex flex-col justify-center">
            <div className="text-rose-600 text-xs sm:text-sm mb-1 font-medium">未打卡筆數</div>
            <div className="text-2xl sm:text-3xl font-bold text-red-600 relative z-10">
              {missedPunches}
              <span className="text-sm sm:text-lg ml-1 text-red-500 font-normal">筆</span>
            </div>
            <XCircle
              className="absolute -right-2 -bottom-2 sm:right-2 sm:top-4 text-rose-200 opacity-50 sm:opacity-100"
              size={48}
            />
          </div>
        </div>

        <div className="overflow-x-auto w-full rounded-lg border border-slate-200 custom-scrollbar -mx-4 sm:mx-0 px-4 sm:px-0">
          <table className="w-full text-left border-collapse min-w-[700px]">
            <thead className="bg-slate-50">
              <tr className="text-slate-600 text-sm">
                <th className="py-3 px-4 font-semibold border-b whitespace-nowrap">員工代號</th>
                <th className="py-3 px-4 font-semibold border-b whitespace-nowrap">員工姓名</th>
                <th className="py-3 px-4 font-semibold border-b whitespace-nowrap">工作日期</th>
                <th className="py-3 px-4 font-semibold border-b text-slate-400 whitespace-nowrap">應出勤</th>
                <th className="py-3 px-4 font-semibold border-b text-slate-400 whitespace-nowrap">應退勤</th>
                <th className="py-3 px-4 font-semibold border-b text-blue-700 whitespace-nowrap">實際出勤</th>
                <th className="py-3 px-4 font-semibold border-b text-blue-700 whitespace-nowrap">實際退勤</th>
                <th className="py-3 px-4 font-semibold border-b text-red-700 whitespace-nowrap">異常狀態</th>
              </tr>
            </thead>

            <tbody>
              {displayRecords.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-slate-500">查無資料</td>
                </tr>
              ) : (
                displayRecords.map((row, i) => {
                  let statusColor = 'text-slate-500';
                  let fontWeight = 'font-normal';

                  if (row.status.includes('申請中') || row.status.includes('審核中')) {
                    statusColor = 'text-blue-600';
                    fontWeight = 'font-bold';
                  } else if (row.status.includes('核准')) {
                    statusColor = 'text-green-600';
                    fontWeight = 'font-bold';
                  } else if (row.status.includes('未打卡')) {
                    statusColor = 'text-red-600';
                    fontWeight = 'font-bold';
                  } else if (row.status.includes('遲到') || row.status.includes('早退')) {
                    statusColor = 'text-orange-500';
                    fontWeight = 'font-bold';
                  }

                  return (
                    <tr key={i} className="text-sm border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-2.5 px-4 text-slate-700 whitespace-nowrap">{pick(row, ['Code'], '')}</td>
                      <td className="py-2.5 px-4 text-slate-700 whitespace-nowrap">{currentUser?.Name}</td>
                      <td className="py-2.5 px-4 text-slate-700 whitespace-nowrap">{row.displayDate}</td>
                      <td className="py-2.5 px-4 text-slate-400 font-mono whitespace-nowrap">{row.displayRSTimestart}</td>
                      <td className="py-2.5 px-4 text-slate-400 font-mono whitespace-nowrap">{row.displayRSTimeend}</td>
                      <td className="py-2.5 px-4 text-blue-700 font-mono font-medium whitespace-nowrap">{row.displayASTimestart}</td>
                      <td className="py-2.5 px-4 text-blue-700 font-mono font-medium whitespace-nowrap">{row.displayASTimeend}</td>
                      <td className={`py-2.5 px-4 ${fontWeight} ${statusColor} whitespace-nowrap`}>
                        {row.status || '正常'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ApplyPunch({ currentUser, db, onUpdateDb, setIsDirty, isDeleteMode }) {
  const [missedPunches, setMissedPunches] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  useEffect(() => {
    if (!currentUser) { setMissedPunches([]); return; }
    const { availablePunches } = getPunchBuckets(db, currentUser);
    setMissedPunches(availablePunches);
  }, [currentUser, db.PERREC, db.MPUNCHH, db.MPUNCHD]);

  const availablePeriods = useMemo(() => {
    const periods = new Set(missedPunches.map(p => {
      const d = parseInt(p.date.substring(8, 10), 10);
      const ym = p.date.substring(0, 7);
      return d <= 15 ? `${ym}前半` : `${ym}後半`;
    }));
    return Array.from(periods).sort((a, b) => b.localeCompare(a));
  }, [missedPunches]);

  useEffect(() => {
    if (availablePeriods.length > 0 && (!selectedPeriod || !availablePeriods.includes(selectedPeriod))) {
      setSelectedPeriod(availablePeriods[0]);
    } else if (availablePeriods.length === 0) {
      setSelectedPeriod('');
    }
  }, [availablePeriods, selectedPeriod]);

  const filteredPunches = useMemo(() => {
    return missedPunches.filter(p => {
      if (!selectedPeriod) return true;
      const d = parseInt(p.date.substring(8, 10), 10);
      const ym = p.date.substring(0, 7);
      const pPeriod = d <= 15 ? `${ym}前半` : `${ym}後半`;
      return pPeriod === selectedPeriod;
    });
  }, [missedPunches, selectedPeriod]);

  useEffect(() => {
    if (setIsDirty) {
      const hasUnsaved = missedPunches.some(item => item.makeupTimestart || item.makeupTimeend || item.reason);
      setIsDirty(hasUnsaved);
    }
  }, [missedPunches, setIsDirty]);

  useEffect(() => {
    return () => { if (setIsDirty) setIsDirty(false); };
  }, [setIsDirty]);

  const handleInputChange = (id, field, value) => {
    setMissedPunches((prev) => prev.map((item) => item.id === id ? { ...item, [field]: value } : item));
  };

  const handleReset = () => {
    const confirmReset = window.confirm("確定要清除所有已填寫的資料嗎？");
    if(!confirmReset) return;
    setMissedPunches((prev) => prev.map((item) => ({ ...item, makeupTimestart: '', makeupTimeend: '', reason: '' })));
  };

  const handleDeleteRecord = async (item) => {
    const confirmDelete = window.confirm(`確定要剔除 ${item.date} 的異常資料嗎？\n(通常用於員工已補請假，無須再補打卡的情況)\n\n⚠️ 警告：剔除後將無法復原！`);
    if (!confirmDelete) return;

    try {
      const targetRecord = (db.PERREC || []).find(r => 
        String(pick(r, ['Code'])).trim() === currentUser?.UserId &&
        normalizeDate(pick(r, ['Sdate'])) === item.date
      );

      if (targetRecord) {
        const newPerrec = (db.PERREC || []).filter(r => r !== targetRecord);
        if (onUpdateDb) {
          await onUpdateDb('PERREC', newPerrec, 'DELETE', targetRecord);
        }
        alert('✅ 該筆資料已成功剔除！');
        setMissedPunches((prev) => prev.filter(p => p.id !== item.id));
      } else {
        alert('找不到該筆資料，可能已被剔除。');
      }
    } catch (err) {
      alert('⚠️ 剔除失敗：' + err.message);
    }
  };

  const deptName = db.GSDEPT?.find((d) => pick(d, ['Code']) === currentUser?.Deptcode)?.Name || currentUser?.Deptcode || '';

  const handleApplyPunch = async () => {
    const allPendingRecords = filteredPunches.filter(item => !item.isSubmitted);
    
    if (allPendingRecords.length === 0) {
      alert('本月份/區間 沒有需要處理的未打卡異常。');
      return;
    }

    const incompleteRecords = allPendingRecords.filter(item => {
  const isMissingIn = item.actualIn === '------' || item.actualIn === '--:--:--' || !item.actualIn;
  const isMissingOut = item.actualOut === '------' || item.actualOut === '--:--:--' || !item.actualOut;
  const bothMissing = isMissingIn && isMissingOut;
  if (bothMissing) {
    // 上下班皆未打卡：只需填事由
    return !item.reason;
  } else {
    // 僅缺上班或僅缺下班：需填補打卡時間及事由
    return (!item.makeupTimestart && !item.makeupTimeend) || !item.reason;
  }
});

    if (incompleteRecords.length > 0) {
      alert('請確認所有異常狀態的「補打卡時間(至少填寫一個)」及「未打卡事由」皆已完整填寫後再送出！');
      return;
    }

    const recordsToSubmit = allPendingRecords;
    const corpRecord = db.GSCORP?.find((c) => pick(c, ['Code']) === currentUser?.Corpcode);
    const actualCorpSno = corpRecord ? pick(corpRecord, ['Sno']) : 1;
    const serno = buildNextPunchSerno(db, currentUser);
    let currentSno = 1;

    try {
      // 組合 details 陣列，透過 create-mpunch-application webhook 送出
      const details = recordsToSubmit.map((item) => ({
        Sdate: item.date,
        NoSwipe_start: item.makeupTimestart || null,
        NoSwipe_end: item.makeupTimeend || null,
        Reason: item.reason,
      }));

      const createPayload = {
        CorpSno: actualCorpSno,
        Code: currentUser?.UserId,
        Deptcode: currentUser?.Deptcode,
        details,
      };

      const createRes = await fetch(`${API_BASE_URL}/create-mpunch-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });

      const createResult = await createRes.json();

      if (!createResult.success) {
        throw new Error(createResult.message || '補打卡申請送出失敗，請稍後再試。');
      }

      // 使用 n8n 回傳的 serno（DB端產生，避免衝突）
      const actualSerno = createResult.serno || serno;
      const submittedDates = recordsToSubmit.map((r) => r.date);

      // 更新本地 PERREC 狀態為「審核中」（MPUNCHH/MPUNCHD 已由 n8n 寫入 DB）
      const nextPerrecRaw = (db.PERREC || []).map((record) => {
        const rDate = normalizeDate(pick(record, ['Sdate']));
        const rUserId = String(pick(record, ['Code'])).trim();

        if (rUserId === String(currentUser?.UserId).trim() && submittedDates.includes(rDate)) {
          const submittedItem = recordsToSubmit.find((r) => r.date === rDate);
          let nextRcode = String(pick(record, ['Rcode']) || '');
          if (nextRcode.includes('未打卡') || nextRcode.includes('未刷卡')) nextRcode = nextRcode.replace(/未打卡/g, '審核中').replace(/未刷卡/g, '審核中');
          else if (!nextRcode.includes('審核中') && !nextRcode.includes('已核准')) nextRcode = nextRcode ? `${nextRcode}、審核中` : '審核中';

          return { ...record, Rcode: nextRcode, Cvertcheck: 1, _serno: actualSerno, _makeupTimeStart: submittedItem?.makeupTimestart || '', _makeupTimeEnd: submittedItem?.makeupTimeend || '', _reason: submittedItem?.reason || '' };
        }
        return record;
      });

      const mergedPerrec = mergePerrecWithPunchStatus({ ...db, PERREC: nextPerrecRaw });
      const changedMergedPerrecRows = mergedPerrec.filter((record) => {
        const rDate = normalizeDate(pick(record, ['Sdate']));
        const rUserId = String(pick(record, ['Code'])).trim();
        return rUserId === String(currentUser?.UserId).trim() && submittedDates.includes(rDate);
      });

      if (onUpdateDb) {
        await onUpdateDb('PERREC', mergedPerrec, 'UPDATE', changedMergedPerrecRows.map(sanitizePerrecForSync));
      }

      alert(`恭喜!!您的補打卡申請資料已成功送出囉^0^\n請通知並等待單位主管進行審核!!\n單號：${actualSerno}`);

      try {
        const targetIds = getApproverIds(db, currentUser);
        if (targetIds.length > 0) {
          fetch(`${API_BASE_URL}/notify-approver`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              applicantId: currentUser.UserId,
              applicantName: currentUser.Name,
              deptName: deptName,
              serno: actualSerno,
              targetUserIds: targetIds
            })
          }).catch(e => console.log('⚠️ [LINE通知] 呼叫 webhook 失敗', e));
        }
      } catch (notifyErr) {
        console.error('⚠️ [LINE通知] 執行失敗:', notifyErr);
      }


      if (setIsDirty) setIsDirty(false);
      setMissedPunches((prev) => prev.filter((item) => !recordsToSubmit.some((r) => r.date === item.date)));
    } catch (error) {
      console.error('handleApplyPunch error:', error);
      alert(`⚠️ 申請送出失敗：${error.message}`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 w-full">
      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-slate-200">
        <div className="flex items-center mb-4 pb-3 sm:pb-4 border-b border-slate-100">
          <User className="text-blue-500 mr-2" size={20} />
          <h3 className="text-base sm:text-lg font-medium text-slate-800">新增補打卡申請 - 基本資訊</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-500 mb-1">申請人</label>
            <div className="px-3 sm:px-4 py-2 bg-slate-50 rounded-lg text-slate-800 border border-slate-200 font-medium text-sm sm:text-base">
              {currentUser?.UserId} - {currentUser?.Name}
            </div>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-500 mb-1">所屬部門</label>
            <div className="px-3 sm:px-4 py-2 bg-slate-50 rounded-lg text-slate-800 border border-slate-200 font-medium text-sm sm:text-base">
              {deptName}
            </div>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-500 mb-1">申請日期</label>
            <div className="px-3 sm:px-4 py-2 bg-slate-50 rounded-lg text-slate-800 border border-slate-200 font-medium text-sm sm:text-base">
              {new Date().toISOString().split('T')[0]}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden w-full">
        <div className="p-4 sm:p-6 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center">
            <AlertTriangle className="text-rose-500 mr-2 shrink-0" size={20} />
            <h3 className="text-base sm:text-lg font-medium text-slate-800">未打卡/異常待辦清單</h3>
            {isDeleteMode && (
              <span className="ml-3 px-2.5 py-1 bg-rose-100 text-rose-700 text-xs font-bold rounded-full flex items-center gap-1 shadow-sm">
                <Trash2 size={14} /> 刪除模式已啟用
              </span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
             <select
               value={selectedPeriod}
               onChange={(e) => setSelectedPeriod(e.target.value)}
               className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium shadow-sm w-full sm:w-auto"
             >
               {availablePeriods.length === 0 && <option value="">無待辦區間</option>}
               {availablePeriods.map(p => (
                 <option key={p} value={p}>{p}</option>
               ))}
             </select>
            <span className="text-xs sm:text-sm text-slate-500 bg-white px-3 py-1.5 rounded-full border border-slate-200 shrink-0 text-center">
              處理中 <strong className="text-slate-800">{filteredPunches.length}</strong> 筆
            </span>
          </div>
        </div>

        <div className="overflow-x-auto w-full custom-scrollbar">
          <table className="w-full text-left text-sm whitespace-nowrap min-w-[800px]">
            <thead>
              <tr className="bg-white text-slate-600 font-medium border-b border-slate-200">
                <th className="px-4 py-3 text-center w-12 sm:w-16">項次</th>
                <th className="px-4 py-3">工作日期</th>
                <th className="px-4 py-3">應出/退勤</th>
                <th className="px-4 py-3">實際出/退勤</th>
                <th className="px-4 py-3 text-rose-600">異常狀態</th>
                <th className="px-4 py-3 w-40 sm:w-48 border-l border-slate-100 bg-blue-50/30">上班/下班 補登</th>
                <th className="px-4 py-3 min-w-[180px] bg-blue-50/30">未打卡事由</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPunches.length === 0 && (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <CheckCircle size={32} className="text-green-400" />
                      <p>太棒了！目前暫無發現任何未打卡的異常紀錄。</p>
                    </div>
                  </td>
                </tr>
              )}

              {filteredPunches.map((item, index) => {
                const actualInMissing = item.actualIn === '------' || item.actualIn === '--:--:--' || !item.actualIn;
                const actualOutMissing = item.actualOut === '------' || item.actualOut === '--:--:--' || !item.actualOut;

                return (
                  <tr key={item.id} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-4 py-3 text-center text-slate-500">
                      {isDeleteMode ? (
                        <button 
                          onClick={() => handleDeleteRecord(item)}
                          className="text-white bg-rose-500 hover:bg-rose-600 p-1.5 rounded-md transition-colors shadow-sm mx-auto flex items-center justify-center"
                          title="直接剔除此異常紀錄 (已請假)"
                        >
                          <X size={16} strokeWidth={3} />
                        </button>
                      ) : (
                        index + 1
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-700">{item.date}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono">
                      {item.expectedIn} ~ {item.expectedOut}
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-mono">
                      <span className={actualInMissing ? 'text-rose-500 font-bold' : ''}>{item.actualIn}</span>
                      {' ~ '}
                      <span className={actualOutMissing ? 'text-rose-500 font-bold' : ''}>{item.actualOut}</span>
                    </td>
                    <td className="px-4 py-3 font-bold text-rose-500">{item.status}</td>

                    <td className="px-3 py-2 border-l border-slate-100 bg-blue-50/10">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-500 w-8">上班</span>
                          <input
                            type="time"
                            value={item.makeupTimestart || ''}
                            onChange={(e) => handleInputChange(item.id, 'makeupTimestart', e.target.value)}
                            disabled={!actualInMissing}
                            className={`w-full px-2 sm:px-3 py-1 border rounded-lg shadow-sm outline-none transition-all ${
                              !actualInMissing 
                                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' 
                                : 'bg-white border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-500 w-8">下班</span>
                          <input
                            type="time"
                            value={item.makeupTimeend || ''}
                            onChange={(e) => handleInputChange(item.id, 'makeupTimeend', e.target.value)}
                            disabled={!actualOutMissing}
                            className={`w-full px-2 sm:px-3 py-1 border rounded-lg shadow-sm outline-none transition-all ${
                              !actualOutMissing 
                                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' 
                                : 'bg-white border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                          />
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-2 bg-blue-50/10 align-middle">
                      <input
                        type="text"
                        placeholder="請輸入事由 (如: 忘記打卡)"
                        value={item.reason}
                        onChange={(e) => handleInputChange(item.id, 'reason', e.target.value)}
                        className="w-full h-[62px] px-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white min-w-[150px]"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
        <button
          onClick={handleReset}
          className="w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl shadow-sm hover:bg-slate-50 font-medium transition-colors"
        >
          取消重填
        </button>
        <button
          onClick={handleApplyPunch}
          className="w-full sm:w-auto flex items-center justify-center px-6 py-3 sm:py-2.5 bg-blue-600 text-white rounded-xl shadow-md hover:bg-blue-700 font-medium transition-transform active:scale-[0.98]"
        >
          <Send size={18} className="mr-2" />
          送出申請
        </button>
      </div>
    </div>
  );
}

function ReportPunchForm({ currentUser, db, onUpdateDb }) {
  const [submittedPunches, setSubmittedPunches] = useState([]);
  const [editingSerno, setEditingSerno] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState('');

  useEffect(() => {
    if (!currentUser) { setSubmittedPunches([]); return; }
    const { submittedPunches } = getPunchBuckets(db, currentUser);
    setSubmittedPunches(submittedPunches);
  }, [currentUser, db.PERREC, db.MPUNCHH, db.MPUNCHD]);

  const availablePeriods = useMemo(() => {
    const periods = new Set(submittedPunches.map(p => {
      const d = parseInt(p.date.substring(8, 10), 10);
      const ym = p.date.substring(0, 7);
      return d <= 15 ? `${ym}前半` : `${ym}後半`;
    }));
    return Array.from(periods).sort((a, b) => b.localeCompare(a));
  }, [submittedPunches]);

  useEffect(() => {
    if (availablePeriods.length > 0 && (!selectedPeriod || !availablePeriods.includes(selectedPeriod))) {
      setSelectedPeriod(availablePeriods[0]);
    } else if (availablePeriods.length === 0) {
      setSelectedPeriod('');
    }
  }, [availablePeriods, selectedPeriod]);

  const filteredSubmitted = useMemo(() => {
    return submittedPunches.filter(p => {
      if (!selectedPeriod) return true;
      const d = parseInt(p.date.substring(8, 10), 10);
      const ym = p.date.substring(0, 7);
      const pPeriod = d <= 15 ? `${ym}前半` : `${ym}後半`;
      return pPeriod === selectedPeriod;
    });
  }, [submittedPunches, selectedPeriod]);

  const handleEditStart = (serno, items) => {
  setEditingSerno(serno);
  const formState = {};
  items.forEach(item => {
    // ✅ 截取 HH:MM，確保 input type="time" 能正確顯示
    const toHHMM = (t) => {
      if (!t || t === '------' || t === '--:--:--') return '';
      const str = String(t).trim();
      // 處理 "HH:MM:SS" → "HH:MM"
      if (/^\d{2}:\d{2}(:\d{2})?$/.test(str)) return str.substring(0, 5);
      return str.substring(0, 5);
    };
    formState[item.id] = {
      makeupTimestart: toHHMM(item.makeupTimestart),
      makeupTimeend: toHHMM(item.makeupTimeend),
      reason: item.reason || ''
    };
  });
  setEditForm(formState);
};

  const handleEditCancel = () => {
    setEditingSerno(null);
    setEditForm({});
  };

  const handleEditSave = async (serno, items) => {
  for (const item of items) {
    const ed = editForm[item.id];
    // ✅ 判斷實際打卡狀態
    const actualInMissing = item.actualIn === '------' || item.actualIn === '--:--:--' || !item.actualIn;
    const actualOutMissing = item.actualOut === '------' || item.actualOut === '--:--:--' || !item.actualOut;
    const bothMissing = actualInMissing && actualOutMissing;

    // ✅ 同 handleApplyPunch 邏輯：兩個都沒打卡 → 只需事由；其中一個沒打 → 需對應補打時間+事由
    const isIncomplete = bothMissing
      ? !ed.reason
      : (!ed.makeupTimestart || !ed.makeupTimeend || !ed.reason);

    if (isIncomplete) {
      alert('補打卡時間(至少填寫一項)與事由皆為必填');
      return;
    }
  }

    const changedMpunchd = [];
    const nextMpunchd = (db.MPUNCHD || []).map(d => {
      if (String(pick(d, ['Serno'])).trim() === serno) {
        const sdate = normalizeDate(pick(d, ['Sdate']));
        const itemMatch = items.find(i => i.date === sdate);
        if (itemMatch) {
          const ed = editForm[itemMatch.id];
          
          const updatedD = {
            ...d,
            NoSwipe_start: ed.makeupTimestart || null,
            NoSwipeStart: ed.makeupTimestart || null,
            NoSwipe_end: ed.makeupTimeend || null,
            NoSwipeEnd: ed.makeupTimeend || null,
            Reason: ed.reason,
            REASON: ed.reason
          };
          const sanitized = sanitizeMpunchDetailForSync(updatedD);
          changedMpunchd.push(sanitized);
          return sanitized;
        }
      }
      return d;
    });

    if (changedMpunchd.length > 0 && onUpdateDb) {
      try {
        await onUpdateDb('MPUNCHD', nextMpunchd, 'UPDATE', changedMpunchd);
        alert('申請資料修改成功！已自動重新送出審核。');
        setEditingSerno(null);
      } catch (err) {
        alert('修改失敗：' + err.message);
      }
    } else {
      alert('無法更新資料：可能是未綁定儲存函式。');
    }
  };

  const handleEditInputChange = (itemId, field, value) => {
    setEditForm(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value }
    }));
  };

  const deptName = db.GSDEPT?.find((d) => pick(d, ['Code']) === currentUser?.Deptcode)?.Name || currentUser?.Deptcode || '';
  const groupedPunches = filteredSubmitted.reduce((acc, item) => {
    const key = item.serno || '歷史舊單(未分類)';
    if (!acc[key]) acc[key] = []; acc[key].push(item); return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 print-container w-full">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 text-center relative overflow-hidden print-no-border flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="w-full sm:w-1/3 flex justify-start hide-on-print">
           <select
             value={selectedPeriod}
             onChange={(e) => setSelectedPeriod(e.target.value)}
             className="border border-slate-300 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium shadow-sm w-full sm:w-auto"
           >
             {availablePeriods.length === 0 && <option value="">無申請紀錄</option>}
             {availablePeriods.map(p => (
               <option key={p} value={p}>{p}</option>
             ))}
           </select>
        </div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-widest text-slate-800 w-full sm:w-1/3">補上、下班打卡紀錄表</h2>
        <div className="w-full sm:w-1/3 flex justify-end">
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center gap-1.5 text-green-700 border-2 sm:border-[3px] border-green-600 rounded-lg sm:rounded-full px-3 py-1.5 sm:px-4 sm:py-1.5 font-bold hover:bg-green-50 transition-colors shadow-sm hide-on-print text-sm sm:text-base w-full sm:w-auto"
          >
            <Printer size={16} strokeWidth={2.5} className="sm:hidden" />
            <Printer size={18} strokeWidth={2.5} className="hidden sm:inline-block" />
            <span className="hidden sm:inline">列印</span>
          </button>
        </div>
      </div>

      {Object.keys(groupedPunches).length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 sm:p-12 text-center text-slate-500">
          此區間目前沒有已送出的補打卡申請。
        </div>
      ) : (
        Object.entries(groupedPunches).map(([serno, items]) => {
          const signSvisorCode = items[0]?.signSvisor;
          const supervisorName = db.GSUSER?.find(u => u.UserId === signSvisorCode)?.Name || signSvisorCode;

          let applicationDate = new Date().toISOString().split('T')[0];
          if (serno.length >= 9 && serno.match(/^\d{8}/)) {
            applicationDate = `${serno.substring(0, 4)}-${serno.substring(4, 6)}-${serno.substring(6, 8)}`;
          }

          const allApproved = items.every((i) => i.isApproved);
          const overallStatus = allApproved ? '已全數核准' : '待審核中';
          const isEditing = editingSerno === serno;

          return (
            <div key={serno} className={`bg-white rounded-xl shadow-sm border overflow-hidden print-no-border mb-6 ${isEditing ? 'border-amber-400 shadow-amber-100/50' : 'border-slate-200'}`}>
              <div className={`p-4 sm:p-6 border-b grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 relative ${isEditing ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>

                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                    <span className="text-slate-500 w-[90px] shrink-0 text-left whitespace-nowrap">申請人：</span>
                    <span className="font-medium text-slate-800 mt-1 sm:mt-0">{currentUser?.UserId} - {currentUser?.Name}</span>
                  </div>
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                    <span className="text-slate-500 w-[90px] shrink-0 text-left whitespace-nowrap">申請日期：</span>
                    <span className="font-medium text-slate-800 mt-1 sm:mt-0">{applicationDate}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                    <span className="text-slate-500 w-[90px] shrink-0 text-left whitespace-nowrap">所屬部門：</span>
                    <span className="font-medium text-slate-800 mt-1 sm:mt-0">{deptName}</span>
                  </div>
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                    <span className="text-slate-500 w-[90px] shrink-0 text-left whitespace-nowrap">申請單號：</span>
                    <span className="font-bold text-blue-700 mt-1 sm:mt-0">{serno}</span>
                  </div>
                </div>

                <div className="flex sm:justify-end">
                  <div className="flex flex-col gap-3 sm:gap-4 w-full sm:w-auto">
                    <div className="text-sm flex flex-col sm:flex-row sm:items-center justify-between sm:justify-start">
                      <div className="flex items-center">
                        <span className="text-slate-500 w-[80px] sm:w-[120px] shrink-0 text-left whitespace-nowrap">狀態：</span>
                        <span className={`font-bold mt-1 sm:mt-0 ${overallStatus === '已全數核准' ? 'text-green-600' : 'text-blue-600'}`}>{overallStatus}</span>
                      </div>

                      {overallStatus === '待審核中' && !isEditing && (
                        <button
                          onClick={() => handleEditStart(serno, items)}
                          className="hide-on-print ml-4 text-xs font-bold text-amber-600 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded flex items-center gap-1 transition-colors"
                        >
                          <Edit size={14} /> 修改內容
                        </button>
                      )}
                      {isEditing && (
                        <div className="hide-on-print ml-4 flex gap-2">
                          <button onClick={() => handleEditSave(serno, items)} className="text-xs font-bold text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded flex items-center gap-1 transition-colors">
                            <Save size={14} /> 儲存
                          </button>
                          <button onClick={handleEditCancel} className="text-xs font-bold text-slate-600 bg-slate-200 hover:bg-slate-300 px-3 py-1.5 rounded flex items-center gap-1 transition-colors">
                            <X size={14} /> 取消
                          </button>
                        </div>
                      )}
                    </div>
                    {supervisorName ? (
                      <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                        <span className="text-slate-500 w-[120px] shrink-0 text-left whitespace-nowrap">審核主管：</span>
                        <span className="font-bold text-green-700 flex items-center gap-1 mt-1 sm:mt-0">
                          <CheckCircle size={14} /> {supervisorName}
                        </span>
                      </div>
                    ) : (
                      <div className="hidden sm:flex text-sm flex-col sm:flex-row sm:items-center invisible">
                        <span className="w-[120px] shrink-0 whitespace-nowrap">佔位符號</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto w-full custom-scrollbar">
                <table className="w-full text-left text-sm whitespace-nowrap min-w-[800px]">
                  <thead>
                    <tr className="bg-white text-slate-600 font-medium border-b border-slate-200">
                      <th className="px-4 py-3 text-center w-12 sm:w-16">項次</th>
                      <th className="px-4 py-3">工作日期</th>
                      <th className="px-4 py-3">應出/退勤</th>
                      <th className="px-4 py-3">實際出/退勤</th>
                      <th className="px-4 py-3 text-rose-600 print-hide-col">異常狀態</th>
                      <th className={`px-4 py-3 w-48 border-l ${isEditing ? 'border-amber-200 bg-amber-50/50' : 'border-slate-100'}`}>上班/下班 補登</th>
                      <th className={`px-4 py-3 min-w-[150px] ${isEditing ? 'bg-amber-50/50' : ''}`}>未打卡事由</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {items.map((item, index) => {
                      const isApprovedStatus = item.isApproved;
                      const statusColor = isApprovedStatus ? 'text-green-600' : 'text-blue-600';
                      const actualInMissing = item.actualIn === '------' || item.actualIn === '--:--:--' || !item.actualIn;
                      const actualOutMissing = item.actualOut === '------' || item.actualOut === '--:--:--' || !item.actualOut;
                      const editData = editForm[item.id];

                      return (
                        <tr key={item.id} className="hover:bg-slate-50 transition-colors group">
                          <td className="px-4 py-3 text-center text-slate-500">{index + 1}</td>
                          <td className="px-4 py-3 font-medium text-slate-700">{item.date}</td>
                          <td className="px-4 py-3 text-slate-500 font-mono">
                            {item.expectedIn} ~ {item.expectedOut}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-mono">
                            <span className={actualInMissing ? 'text-rose-500 font-bold' : ''}>{item.actualIn}</span>
                            {' ~ '}
                            <span className={actualOutMissing ? 'text-rose-500 font-bold' : ''}>{item.actualOut}</span>
                          </td>

                          <td className={`px-4 py-3 font-bold ${statusColor} print-hide-col`}>{item.status}</td>

                          <td className={`px-3 py-2 border-l ${isEditing ? 'border-amber-200 bg-amber-50/20' : 'border-slate-100'}`}>
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500 w-8">上班</span>
                                {isEditing ? (
                                  <input
                                    type="time"
                                    value={editData?.makeupTime_start || ''}
                                    onChange={(e) => handleEditInputChange(item.id, 'makeupTimestart', e.target.value)}
                                    disabled={!actualInMissing}
                                    className={`w-full px-2 py-1 border rounded outline-none font-mono text-sm ${
                                      !actualInMissing
                                        ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                        : 'bg-white border-amber-400 focus:ring-2 focus:ring-amber-400'
                                    }`}
                                  />
                                ) : (
                                  <span className={`font-mono px-2 py-1 rounded border w-full text-sm h-[30px] flex items-center ${
                                    !actualInMissing ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-slate-50 text-slate-700 border-slate-200'
                                  }`}>
                                    {item.makeupTimestart || '--:--'}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500 w-8">下班</span>
                                {isEditing ? (
                                  <input
                                    type="time"
                                    value={editData?.makeupTime_end || ''}
                                    onChange={(e) => handleEditInputChange(item.id, 'makeupTimeend', e.target.value)}
                                    disabled={!actualOutMissing}
                                    className={`w-full px-2 py-1 border rounded outline-none font-mono text-sm ${
                                      !actualOutMissing
                                        ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                        : 'bg-white border-amber-400 focus:ring-2 focus:ring-amber-400'
                                    }`}
                                  />
                                ) : (
                                  <span className={`font-mono px-2 py-1 rounded border w-full text-sm h-[30px] flex items-center ${
                                    !actualOutMissing ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-slate-50 text-slate-700 border-slate-200'
                                  }`}>
                                    {item.makeupTimeend || '--:--'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className={`px-3 py-2 align-middle ${isEditing ? 'bg-amber-50/20' : ''}`}>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editData?.reason || ''}
                                onChange={(e) => handleEditInputChange(item.id, 'reason', e.target.value)}
                                placeholder="請輸入修改事由"
                                className="w-full px-3 h-[66px] border border-amber-400 rounded-lg shadow-sm outline-none bg-white focus:ring-2 focus:ring-amber-400"
                              />
                            ) : (
                              <div className="w-full px-3 py-2 border rounded-lg shadow-sm bg-slate-50 text-slate-600 border-slate-200 min-h-[66px] whitespace-normal break-words flex items-center">
                                {item.reason}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function ApprovePunchForm({ currentUser, db, onUpdateDb }) {
  const [approvalPunches, setApprovalPunches] = useState([]);

  useEffect(() => {
    if (!currentUser) { setApprovalPunches([]); return; }
    const buckets = getApprovalBuckets(db, currentUser);
    setApprovalPunches(buckets);
  }, [currentUser, db.PERREC, db.MPUNCHH, db.MPUNCHD, db.GSUSER]);

  const handleApprovalToggle = async (dateToToggle, targetUserId) => {
    if (!onUpdateDb) return;

    const targetDate = normalizeDate(dateToToggle);
    const userIdStr = String(targetUserId).trim();

    const matchedHeader = (db.MPUNCHH || []).find((h) => {
      const hUserId = String(pick(h, ['Code', 'UserId'])).trim();
      if (hUserId !== userIdStr) return false;
      const serno = String(pick(h, ['Serno'])).trim();
      if (!serno) return false;
      return (db.MPUNCHD || []).some((d) => String(pick(d, ['Serno'])).trim() === serno && normalizeDate(pick(d, ['Sdate'])) === targetDate);
    });

    if (!matchedHeader) { alert('找不到對應的補打卡申請表頭(MPUNCHH)，無法核准。'); return; }
    const targetSerno = String(pick(matchedHeader, ['Serno'])).trim();

    const targetDetail = (db.MPUNCHD || []).find((d) => String(pick(d, ['Serno'])).trim() === targetSerno && normalizeDate(pick(d, ['Sdate'])) === targetDate);
    if (!targetDetail) { alert('找不到對應的補打卡申請明細(MPUNCHD)，無法核准。'); return; }

    const currentSignCheck = String(pick(targetDetail, ['Sign_check'])).trim();
    const nextApproved = !normalizeApprovalFlag(currentSignCheck);

    let changedMpunchd = null;
    const newMpunchd = (db.MPUNCHD || []).map((d) => {
      const dSerno = String(pick(d, ['Serno'])).trim();
      const dDate = normalizeDate(pick(d, ['Sdate']));
      if (dSerno === targetSerno && dDate === targetDate) {
        const updated = sanitizeMpunchDetailForSync({ ...d, Sign_check: nextApproved ? 1 : 0 });
        changedMpunchd = updated; return updated;
      }
      return d;
    });

    const allItemsForSerno = newMpunchd.filter(d => String(pick(d, ['Serno'])).trim() === targetSerno);
    const allItemsApproved = allItemsForSerno.every(d => normalizeApprovalFlag(pick(d, ['Sign_check'])));
    
    let changedMpunchh = null;
    const newMpunchh = (db.MPUNCHH || []).map((h) => {
        if (String(pick(h, ['Serno'])).trim() === targetSerno) {
            const nextSvisor = allItemsApproved ? String(currentUser?.UserId || '').trim() : '';
            if (String(pick(h, ['SignSvisor'])).trim() !== nextSvisor) {
                const updatedH = sanitizeMpunchHeaderForSync({ ...h, SignSvisor: nextSvisor });
                changedMpunchh = updatedH; return updatedH;
            }
        }
        return h;
    });

    let changedPerrec = null;
    const newPerrecRaw = (db.PERREC || []).map((record) => {
      const rUserId = String(pick(record, ['Code'])).trim();
      const rDate = normalizeDate(pick(record, ['Sdate']));
      if (rUserId === userIdStr && rDate === targetDate) {
        let nextRcode = String(pick(record, ['Rcode']) || '');
        if (nextApproved) {
          if (nextRcode.includes('審核中')) nextRcode = nextRcode.replace(/審核中/g, '已核准');
          else if (!nextRcode.includes('已核准')) nextRcode = nextRcode ? `${nextRcode}、已核准` : '已核准';
        } else {
          if (nextRcode.includes('已核准')) nextRcode = nextRcode.replace(/已核准/g, '審核中');
          else if (!nextRcode.includes('審核中')) nextRcode = nextRcode ? `${nextRcode}、審核中` : '審核中';
        }
        const updated = { ...record, Rcode: nextRcode };
        changedPerrec = updated; return updated;
      }
      return record;
    });

    const nextDb = { ...db, MPUNCHH: newMpunchh, MPUNCHD: newMpunchd, PERREC: newPerrecRaw };
    const mergedPerrec = mergePerrecWithPunchStatus(nextDb);

    try {
      if (changedMpunchd) await onUpdateDb('MPUNCHD', newMpunchd, 'UPDATE', changedMpunchd);
      else await onUpdateDb('MPUNCHD', newMpunchd);

      if (changedMpunchh) await onUpdateDb('MPUNCHH', newMpunchh, 'UPDATE', changedMpunchh);
      else await onUpdateDb('MPUNCHH', newMpunchh);

      if (changedPerrec) {
        const syncedPerrec = sanitizePerrecForSync({
          ...changedPerrec,
          Rcode: mergedPerrec.find((r) => String(pick(r, ['Code'])).trim() === userIdStr && normalizeDate(pick(r, ['Sdate'])) === targetDate)?.Rcode || changedPerrec.Rcode
        });
        await onUpdateDb('PERREC', mergedPerrec, 'UPDATE', syncedPerrec);
      } else {
        await onUpdateDb('PERREC', mergedPerrec);
      }
    } catch (error) {
      console.error(error); alert('⚠️ 核准狀態更新失敗: ' + error.message);
    }
  };

  const groupedPunches = approvalPunches.reduce((acc, item) => {
    const key = item.serno || '歷史舊單(未分類)';
    if (!acc[key]) acc[key] = []; acc[key].push(item); return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 print-container w-full">
      <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 p-6 sm:p-8 text-center relative overflow-hidden print-no-border">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-widest text-white flex items-center justify-center gap-3">
          <CheckCircle size={28} className="text-blue-400" />
          部屬待核准打卡單
        </h2>
      </div>

      {Object.keys(groupedPunches).length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 sm:p-12 text-center text-slate-500">
          目前沒有任何需要您審核的補打卡申請。
        </div>
      ) : (
        Object.entries(groupedPunches).map(([serno, items]) => {
          const applicantUserId = items[0]?.userId;
          const applicantName = items[0]?.userName;
          const applicantDept = db.GSDEPT?.find((d) => pick(d, ['Code']) === items[0]?.deptCode)?.Name || items[0]?.deptCode;
          
          let applicationDate = items[0]?.date || new Date().toISOString().split('T')[0];
          if (serno.length >= 9 && serno.match(/^\d{8}/)) {
            applicationDate = `${serno.substring(0, 4)}-${serno.substring(4, 6)}-${serno.substring(6, 8)}`;
          }

          const allApproved = items.every((i) => i.isApproved);
          const overallStatus = allApproved ? '已全數核准' : '待審核中';

          return (
            <div key={serno} className="bg-white rounded-xl shadow-sm border-2 border-blue-100 overflow-hidden mb-6 hover:shadow-md transition-shadow">
              <div className="p-4 sm:p-6 border-b border-blue-100 bg-blue-50/50 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 relative">
                
                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                    <span className="text-slate-500 w-[90px] shrink-0 text-left whitespace-nowrap">申請人：</span>
                    <span className="font-bold text-slate-800 text-base mt-1 sm:mt-0">{applicantUserId} - {applicantName}</span>
                  </div>
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                    <span className="text-slate-500 w-[90px] shrink-0 text-left whitespace-nowrap">申請單號：</span>
                    <span className="font-bold text-blue-700 mt-1 sm:mt-0">{serno}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                    <span className="text-slate-500 w-[90px] shrink-0 text-left whitespace-nowrap">所屬部門：</span>
                    <span className="font-medium text-slate-800 mt-1 sm:mt-0">{applicantDept}</span>
                  </div>
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                    <span className="text-slate-500 w-[90px] shrink-0 text-left whitespace-nowrap">提出日期：</span>
                    <span className="font-medium text-slate-800 mt-1 sm:mt-0">{applicationDate}</span>
                  </div>
                </div>
                
                <div className="flex sm:justify-end">
                  <div className="flex flex-col gap-3 sm:gap-4 w-full sm:w-auto">
                    <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                      <span className="text-slate-500 w-[120px] shrink-0 text-left whitespace-nowrap">審核狀態：</span>
                      <span className={`font-bold mt-1 sm:mt-0 ${overallStatus === '已全數核准' ? 'text-green-600' : 'text-blue-600'}`}>{overallStatus}</span>
                    </div>
                    {allApproved ? (
                      <div className="text-sm flex flex-col sm:flex-row sm:items-center">
                        <span className="text-slate-500 w-[120px] shrink-0 text-left whitespace-nowrap">審核完成時間：</span>
                        <span className="font-medium text-green-600 mt-1 sm:mt-0">{applicationDate}</span>
                      </div>
                    ) : (
                      <div className="hidden sm:flex text-sm flex-col sm:flex-row sm:items-center invisible">
                        <span className="w-[120px] shrink-0 whitespace-nowrap">佔位符號</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto w-full custom-scrollbar">
                <table className="w-full text-left text-sm whitespace-nowrap min-w-[800px]">
                  <thead>
                    <tr className="bg-white text-slate-600 font-medium border-b border-slate-200">
                      <th className="px-4 py-3 text-center w-12 sm:w-16">項次</th>
                      <th className="px-4 py-3">工作日期</th>
                      <th className="px-4 py-3">應出/退勤</th>
                      <th className="px-4 py-3">實際出/退勤</th>
                      <th className="px-4 py-3 text-rose-600 print-hide-col">異常狀態</th>
                      <th className="px-4 py-3 w-48 border-l border-slate-100">上班/下班 補登</th>
                      <th className="px-4 py-3 min-w-[150px]">未打卡事由</th>
                      <th className="px-4 py-3 text-center w-28 border-l border-blue-100 bg-blue-50 text-blue-800">
                        點擊核准
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {items.map((item, index) => {
                      const isApprovedStatus = item.isApproved;
                      const statusColor = isApprovedStatus ? 'text-green-600' : 'text-blue-600';
                      const actualInMissing = item.actualIn === '------' || item.actualIn === '--:--:--' || !item.actualIn;
                      const actualOutMissing = item.actualOut === '------' || item.actualOut === '--:--:--' || !item.actualOut;

                      return (
                        <tr key={item.id} className="hover:bg-slate-50 transition-colors group">
                          <td className="px-4 py-3 text-center text-slate-500">{index + 1}</td>
                          <td className="px-4 py-3 font-medium text-slate-700">{item.date}</td>
                          <td className="px-4 py-3 text-slate-500 font-mono">
                            {item.expectedIn} ~ {item.expectedOut}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-mono">
                            <span className={actualInMissing ? 'text-rose-500 font-bold' : ''}>{item.actualIn}</span>
                            {' ~ '}
                            <span className={actualOutMissing ? 'text-rose-500 font-bold' : ''}>{item.actualOut}</span>
                          </td>

                          <td className={`px-4 py-3 font-bold ${statusColor} print-hide-col`}>{item.status}</td>

                          <td className="px-3 py-2 border-l border-slate-100">
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500 w-8">上班</span>
                                <span className={`font-mono px-2 py-1 rounded border w-full text-sm h-[30px] flex items-center ${
                                  !actualInMissing ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-slate-50 text-slate-700 border-slate-200'
                                }`}>
                                  {item.makeupTimestart || '--:--'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500 w-8">下班</span>
                                <span className={`font-mono px-2 py-1 rounded border w-full text-sm h-[30px] flex items-center ${
                                  !actualOutMissing ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-slate-50 text-slate-700 border-slate-200'
                                }`}>
                                  {item.makeupTimeend || '--:--'}
                                </span>
                              </div>
                            </div>
                          </td>

                          <td className="px-3 py-2 align-middle">
                            <div className="w-full px-3 py-2 border rounded-lg shadow-sm bg-slate-50 text-slate-600 border-slate-200 min-h-[66px] whitespace-normal break-words flex items-center">
                              {item.reason}
                            </div>
                          </td>

                          <td className="px-4 py-2 text-center border-l border-blue-100 bg-blue-50/50 print-hide-col">
                            <label className="inline-flex items-center justify-center w-full h-full cursor-pointer group-hover:scale-105 transition-transform">
                              <div className="relative flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={item.isApproved}
                                  onChange={() => handleApprovalToggle(item.date, item.userId)}
                                  className="sr-only"
                                />
                                <div
                                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                                    item.isApproved
                                      ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-200'
                                      : 'bg-white border-2 border-blue-200 text-transparent shadow-sm hover:border-blue-400'
                                  }`}
                                >
                                  <Check size={20} strokeWidth={3.5} />
                                </div>
                              </div>
                            </label>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function ReportPunchStats({ currentUser, db, onUpdateDb }) {
  const isSuperManager = currentUser?.Manager === 1 || currentUser?.Manager === '1' || currentUser?.Manager === true ||
                         currentUser?.GM === 1 || currentUser?.GM === '1' || currentUser?.GM === true;

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedDept, setSelectedDept] = useState(currentUser?.Deptcode || '');

  const allMonthsSet = new Set();
  const allPunches = [];
  
  (db.MPUNCHD || []).forEach(d => {
    const h = (db.MPUNCHH || []).find(h => String(pick(h, ['Serno'])).trim() === String(pick(d, ['Serno'])).trim());
    if (!h) return;

    const sdate = normalizeDate(pick(d, ['Sdate']));
    if (!sdate) return;
    
    const monthStr = sdate.substring(0, 7);
    allMonthsSet.add(monthStr);

    allPunches.push({
      date: sdate,
      month: monthStr,
      day: parseInt(sdate.substring(8, 10), 10),
      userId: String(pick(h, ['Code', 'UserId'])).trim(),
      deptCode: String(pick(h, ['Deptcode'])).trim(),
      in: normalizeTime(pick(d, ['NoSwipe_start', 'NoSwipeStart'])).substring(0, 5),
      out: normalizeTime(pick(d, ['NoSwipe_end', 'NoSwipeEnd'])).substring(0, 5),
      reason: String(pick(d, ['Reason', 'reason']) || '').trim(),
      serno: String(pick(h, ['Serno'])).trim()
    });
  });

  const availableMonths = Array.from(allMonthsSet).sort((a, b) => b.localeCompare(a));

  const filteredPunches = allPunches.filter(p => {
    if (p.month !== selectedMonth) return false;
    if (isSuperManager) {
      if (selectedDept && p.deptCode !== selectedDept) return false;
    } else {
      if (p.deptCode !== currentUser?.Deptcode) return false;
    }
    return true;
  });

  const punchMap = {};
  const usersSet = new Set();
  
  filteredPunches.forEach(p => {
     if (!punchMap[p.userId]) punchMap[p.userId] = {};
     if (!punchMap[p.userId][p.day]) punchMap[p.userId][p.day] = { in: '', out: '', reason: '' };
     
     let t1 = p.in && p.in !== '--:--' ? p.in : '';
     let t2 = p.out && p.out !== '--:--' ? p.out : '';
     let finalIn = '';
     let finalOut = '';
     
     if (t1 && t2) {
       if (t1 <= t2) { finalIn = t1; finalOut = t2; }
       else { finalIn = t2; finalOut = t1; }
     } else if (t1) {
       if (t1 < '12:00') finalIn = t1;
       else finalOut = t1;
     } else if (t2) {
       if (t2 < '12:00') finalIn = t2;
       else finalOut = t2;
     }
     
     if (finalIn) punchMap[p.userId][p.day].in = finalIn;
     if (finalOut) punchMap[p.userId][p.day].out = finalOut;
     if (p.reason) punchMap[p.userId][p.day].reason = p.reason;
     
     usersSet.add(p.userId);
  });

  const usersInReport = Array.from(usersSet).map(uid => {
     const u = (db.GSUSER || []).find(x => String(pick(x, ['UserId'])).trim() === uid);
     return { userId: uid, userName: u ? pick(u, ['Name']) : '' };
  }).sort((a, b) => a.userId.localeCompare(b.userId));

  const chunkArray = (arr, size) => {
    return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
      arr.slice(i * size, i * size + size)
    );
  };
  
  const paddedChunks = chunkArray(usersInReport, 3).map(chunk => {
    const newChunk = [...chunk];
    while (newChunk.length < 3) {
      newChunk.push(null);
    }
    return newChunk;
  });

  const yearStr = selectedMonth ? selectedMonth.substring(0, 4) : '';
  const monthStr = selectedMonth ? selectedMonth.substring(5, 7) : '';
  const displayTitle = selectedMonth ? `${yearStr}(${monthStr})月份－補上、下班打卡統計表` : '補上、下班打卡統計表';
  
  const corpObj = db.GSCORP?.find(c => pick(c, ['Code']) === currentUser?.Corpcode);
  const corpName = corpObj ? pick(corpObj, ['Name']) : '';
  const deptObj = db.GSDEPT?.find(d => pick(d, ['Code']) === selectedDept);
  const deptNameDisplay = deptObj ? pick(deptObj, ['Name']) : selectedDept || '';

  const handleEmpCheckToggle = async (targetUserId, sernosToUpdate, newCheckStatus) => {
    if (targetUserId !== currentUser?.UserId) {
      alert('您只能確認自己的補打卡紀錄！');
      return;
    }
    if (!onUpdateDb) return;
    
    const confirmMsg = newCheckStatus ? "確定要確認本月份補打卡資料無誤嗎？" : "確定要取消確認嗎？";
    if (!window.confirm(confirmMsg)) return;

    let updatedHeaders = [];
    const newMpunchh = (db.MPUNCHH || []).map(h => {
      const s = String(pick(h, ['Serno'])).trim();
      if (sernosToUpdate.includes(s)) {
         const updatedH = sanitizeMpunchHeaderForSync({ ...h, EmpCheck: newCheckStatus ? 1 : 0 });
         updatedHeaders.push(updatedH);
         return updatedH;
      }
      return h;
    });

    try {
      await onUpdateDb('MPUNCHH', newMpunchh, 'UPDATE', updatedHeaders);
      alert('✅ 確認狀態已更新！');
    } catch (error) {
      alert('⚠️ 狀態更新失敗: ' + error.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 print-container w-full">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-8 relative print-no-border">
        
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6 sm:mb-8 hide-on-print">
          <div className="flex flex-col sm:flex-row gap-3">
            <select 
              value={selectedMonth} 
              onChange={e => setSelectedMonth(e.target.value)} 
              className="border border-slate-300 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 font-medium"
            >
              <option value={selectedMonth}>{selectedMonth} (目前選擇)</option>
              {availableMonths.map(m => m !== selectedMonth && <option key={m} value={m}>{m}</option>)}
            </select>
            
            {isSuperManager && (
              <select 
                value={selectedDept} 
                onChange={e => setSelectedDept(e.target.value)} 
                className="border border-slate-300 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 font-medium"
              >
                <option value="">選擇跨部門 (全部隱藏)</option>
                {(db.GSDEPT || []).map(d => (
                  <option key={pick(d, ['Code'])} value={pick(d, ['Code'])}>
                    {pick(d, ['Name'])}
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <button
            onClick={() => window.print()}
            disabled={usersInReport.length === 0}
            className={`flex items-center justify-center gap-1.5 px-4 py-2 font-bold rounded-lg transition-colors border-2 ${
              usersInReport.length === 0 
                ? 'border-slate-300 text-slate-400 cursor-not-allowed bg-slate-50' 
                : 'text-green-700 border-green-600 hover:bg-green-50 shadow-sm'
            }`}
          >
            <Printer size={18} strokeWidth={2.5} /> 列印此報表
          </button>
        </div>

        {usersInReport.length === 0 ? (
          <div className="text-center py-16 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl hide-on-print">
            <BarChart2 className="mx-auto text-slate-400 mb-3" size={32} />
            <p className="text-slate-500 text-lg font-medium">此月份該部門無任何員工送出補打卡申請。</p>
            <p className="text-slate-400 text-sm mt-1">請透過上方的下拉選單切換月份或部門。</p>
          </div>
        ) : (
          paddedChunks.map((chunk, chunkIndex) => (
            <div key={`page-${chunkIndex}`} className={`w-full print-overflow-visible break-after-page`} style={{overflowX: 'auto'}}>
              <h2 className="text-2xl sm:text-[28px] font-bold text-center mb-6 sm:mb-8 font-serif tracking-widest text-black">
                {displayTitle}
              </h2>
              
              <div className="flex justify-between mb-2 text-sm sm:text-base font-serif px-1 text-black font-medium">
                <div>公司名稱：{corpName}</div>
                <div>所屬部門：{deptNameDisplay}</div>
              </div>
              
              <table className="w-full border-collapse border-2 border-black text-center font-serif table-fixed" style={{fontSize:'13px', lineHeight:'1.6'}}>
                <thead>
                  <tr>
                    <th className="border border-black p-1 w-12 whitespace-nowrap" rowSpan={2}>日期</th>
                    {chunk.map((u, i) => (
                      <th key={u ? u.userId : `empty-${i}`} className="border border-black p-1 whitespace-nowrap w-[30%]" colSpan={2}>
                        {u ? `${u.userId} ${u.userName}` : '\u00A0'}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {chunk.map((u, i) => (
                      <React.Fragment key={(u ? u.userId : `empty-${i}`) + '-cols'}>
                        <th className="border border-black p-0.5 w-[15%] font-normal whitespace-nowrap">上班時間</th>
                        <th className="border border-black p-0.5 w-[15%] font-normal whitespace-nowrap">下班時間</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...Array(31)].map((_, i) => {
                    const day = i + 1;
                    return (
                      <tr key={day}>
                        <td className="border border-black p-0.5">{day}</td>
                        {chunk.map((u, ui) => {
                          const punch = u ? (punchMap[u.userId]?.[day] || { in: '', out: '' }) : { in: '', out: '' };
                          return (
                            <React.Fragment key={(u ? u.userId : `empty-${ui}`) + day}>
                              {(!punch.in && !punch.out && punch.reason) ? (
                                <td className="border border-black p-0.5 text-red-600 font-medium" colSpan={2} style={{wordBreak:'break-all'}}>{punch.reason}</td>
                              ) : (
                                <>
                                  <td className="border border-black p-0.5 text-red-600 font-mono">{punch.in}</td>
                                  <td className="border border-black p-0.5 text-red-600 font-mono">{punch.out}</td>
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                  <tr>
                     <td className="border border-black p-1 whitespace-nowrap font-medium tracking-widest bg-slate-50">員工簽名</td>
                     {chunk.map((u, ui) => {
                        if (!u) return <td key={`empty-${ui}-sig`} className="border border-black p-2 sm:p-3" colSpan={2}></td>;
                        
                        const userSernosInMonth = allPunches.filter(p => p.userId === u.userId && p.month === selectedMonth).map(p => p.serno);
                        const uniqueSernos = [...new Set(userSernosInMonth)];
                        const headers = (db.MPUNCHH || []).filter(h => uniqueSernos.includes(String(pick(h, ['Serno'])).trim()));
                        
                        const isAllChecked = headers.length > 0 && headers.every(h => normalizeApprovalFlag(pick(h, ['EmpCheck'])));
                        const isMe = u.userId === currentUser?.UserId;

                        return (
                          <td key={`${u.userId}-sig`} className="border border-black p-2 sm:p-3" colSpan={2}>
                             <label className={`inline-flex items-center justify-center gap-1.5 sm:gap-2 ${isMe ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed opacity-50'}`} title={isMe ? "點擊確認您的資料" : "僅限員工本人勾選"}>
                               <div className="relative flex items-center justify-center">
                                 <input 
                                   type="checkbox" 
                                   checked={isAllChecked} 
                                   onChange={(e) => handleEmpCheckToggle(u.userId, uniqueSernos, e.target.checked)}
                                   disabled={!isMe}
                                   className="sr-only"
                                 />
                                 <div className={`w-5 h-5 sm:w-6 sm:h-6 border-[3px] border-black flex items-center justify-center bg-white`}>
                                    {isAllChecked && <Check size={18} strokeWidth={4} className="text-black" />}
                                 </div>
                               </div>
                               <span className="font-bold text-sm sm:text-base tracking-widest whitespace-nowrap text-black">本人確認資料無誤</span>
                             </label>
                          </td>
                        )
                     })}
                  </tr>
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ==========================================
// 6. Admin E Import
// ==========================================
function AdminImport({ db, setIsDirty, onImportSuccess }) {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [rawParsedData, setRawParsedData] = useState([]); // ✅ 改為保留完整清洗紀錄
  const [errorMsg, setErrorMsg] = useState('');
  const [encoding, setEncoding] = useState('auto');
  const [showDebug, setShowDebug] = useState(false);
  const [formatType, setFormatType] = useState('shinkong');

  const [exportMonth, setExportMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedUsersExport, setSelectedUsersExport] = useState([]);
  const [excludeNoData, setExcludeNoData] = useState(false); 

  const [defaultYear, setDefaultYear] = useState(new Date().getFullYear().toString());
  const [filterNoDataUser, setFilterNoDataUser] = useState(false); 

  const loadXLSX = async () => {
    if (window.XLSX) return window.XLSX;
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        script.onload = () => resolve(window.XLSX);
        script.onerror = reject;
        document.body.appendChild(script);
    });
  };

  const availableExportMonths = useMemo(() => {
    const months = new Set();
    (db?.PERREC || []).forEach(r => {
      const d = normalizeDate(pick(r, ['Sdate']));
      if (d && d.length >= 7) months.add(d.substring(0, 7));
    });
    return Array.from(months).sort((a, b) => b.localeCompare(a));
  }, [db?.PERREC]);

  useEffect(() => {
    if (availableExportMonths.length > 0 && !availableExportMonths.includes(exportMonth)) {
      setExportMonth(availableExportMonths[0]);
    }
  }, [availableExportMonths, exportMonth]);

  const displayExportMonths = availableExportMonths.length > 0 ? availableExportMonths : [exportMonth];

  const isApprovedLeaveNoPunchRow = (row) => {
    const leaveStart = String(row.LeaveStart || row.LeaveStartTime || row['請假開始時間'] || '').trim();
    const leaveEnd = String(row.LeaveEnd || row.LeaveEndTime || row['請假結束時間'] || '').trim();
    const leaveType = String(row.LeaveType || row['假別'] || '').trim();
    const lateMins = Number(row.LateMins ?? row['遲到'] ?? 0);
    const earlyMins = Number(row.EarlyMins ?? row['早退'] ?? 0);
    const absentMins = Number(row.AbsentMins ?? row['曠職'] ?? 0);
    const aStart = String(row.ASTime_start || '').trim();
    const aEnd = String(row.ASTime_end || '').trim();

    const hasIn = !!aStart && aStart !== '00:00:00' && aStart !== '00:00';
    const hasOut = !!aEnd && aEnd !== '00:00:00' && aEnd !== '00:00';
    const hasLeaveInfo = !!leaveType || !!leaveStart || !!leaveEnd;

    return !hasIn && !hasOut && hasLeaveInfo && lateMins === 0 && earlyMins === 0 && absentMins === 0;
  };

  // ✅ 使用動態 useMemo 以即時套用「過濾員工」勾選邏輯與「單邊缺卡自動判定未打卡」
  const parsedData = useMemo(() => {
    let data = rawParsedData.map(row => {
      let rcode = null;
      const aStart = row.ASTime_start;
      const aEnd = row.ASTime_end;
      const rStart = row.RSTime_start;
      const rEnd = row.RSTime_end;

      const isLeaveDay = isApprovedLeaveNoPunchRow(row);
      rcode = isLeaveDay ? null : buildCombinedAbnormalStatus(row);

      return { ...row, Rcode: rcode };
    });

    data = data.filter(row => !isApprovedLeaveNoPunchRow(row));

    if (filterNoDataUser) {
      const userHasDataMap = new Map();
      data.forEach(row => {
        const code = String(row.Code || '').trim();
        if (!userHasDataMap.has(code)) userHasDataMap.set(code, false);

        const hasIn = row.ASTime_start && row.ASTime_start !== '00:00:00' && row.ASTime_start !== '00:00';
        const hasOut = row.ASTime_end && row.ASTime_end !== '00:00:00' && row.ASTime_end !== '00:00';
        const hasPunch = hasIn || hasOut;
        const isPartialAbsent = Number(row.AbsentMins || 0) > 0 && Number(row.AbsentMins || 0) !== 480 && Number(row.AbsentMins || 0) !== 450;

        if (hasPunch || isPartialAbsent) {
          userHasDataMap.set(code, true);
        }
      });
      data = data.filter(row => userHasDataMap.get(String(row.Code || '').trim()));
    }

    return data;
  }, [rawParsedData, filterNoDataUser]);

  useEffect(() => {
    if (setIsDirty) setIsDirty(rawParsedData.length > 0);
  }, [rawParsedData, setIsDirty]);

  useEffect(() => {
    return () => { if (setIsDirty) setIsDirty(false); };
  }, [setIsDirty]);

  // 💡 修正 1：嚴格判斷時間字串，排除 00:00 等非打卡格式與幽靈時間
  const formatTimeStr = (t) => {
    if (!t) return '';
    let str = String(t).trim();
    if (str === '' || str === 'null' || str === '未打卡' || str.includes('::')) return '';
    if (str === '00:00' || str === '00:00:00') return ''; // 排除系統預設的幽靈時間

    // 已符合 HH:mm 或 HH:mm:ss 格式
    if (/^([01]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(str)) {
       const parts = str.split(':');
       const h = parts[0].padStart(2, '0');
       const m = parts[1];
       const s = parts[2] ? parts[2].padStart(2, '0') : '00';
       return `${h}:${m}:${s}`;
    }

    // 處理純數字 (例如：0800, 1730)
    let digits = str.replace(/\D/g, '');
    if (!digits || digits.length < 3) return ''; 
    // 強制排除常見的曠職分鐘數或幽靈數字被當成打卡時間的問題
    if (digits === '480' || digits === '450' || digits === '0000' || digits === '000000') return ''; 

    if (digits.length === 3) digits = '0' + digits;
    if (digits.length === 4) {
       const h = parseInt(digits.substring(0, 2), 10);
       const m = parseInt(digits.substring(2, 4), 10);
       if (h < 24 && m < 60) return `${digits.substring(0, 2)}:${digits.substring(2, 4)}:00`;
    }
    if (digits.length >= 6) {
       const h = parseInt(digits.substring(0, 2), 10);
       const m = parseInt(digits.substring(2, 4), 10);
       const s = parseInt(digits.substring(4, 6), 10);
       if (h < 24 && m < 60 && s < 60) return `${digits.substring(0, 2)}:${digits.substring(2, 4)}:${digits.substring(4, 6)}`;
    }
    return '';
  };

  const toNumber = (value) => {
    const cleaned = String(value ?? '').replace(/[^\d.-]/g, '').trim();
    if (!cleaned) return 0;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
  };

  const extractCleanDate = (rawStr, fallbackYear) => {
    if (!rawStr) return '';
    const cleanStr = rawStr.trim();
    const currentYear = fallbackYear || new Date().getFullYear();
    
    if (/^\d{1,2}[\/\-]\d{1,2}(?!\d)/.test(cleanStr)) {
       const parts = cleanStr.split(/[\/\-]/);
       const y = String(currentYear);
       const m = parts[0].replace(/\D/g, '');
       const d = parts[1].replace(/\D/g, '').substring(0, 2);
       if (m && d) return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }

    const parts = cleanStr.split(/[\/\-]/);
    let y, m, d;
    
    if (parts.length >= 3) {
       y = parts[0].replace(/\D/g, '');
       m = parts[1].replace(/\D/g, '');
       d = parts[2].replace(/\D/g, '').substring(0, 2);
       
       if (y.length <= 2 && parts[2].replace(/\D/g, '').length >= 4) {
         y = parts[2].replace(/\D/g, '').substring(0, 4);
         m = parts[0].replace(/\D/g, '');
         d = parts[1].replace(/\D/g, '');
       }
       
       if (y.length === 3) y = String(parseInt(y, 10) + 1911); 
       if (y.length === 2) y = "20" + y; 
    } else if (parts.length === 2) {
       y = String(currentYear);
       m = parts[0].replace(/\D/g, '');
       d = parts[1].replace(/\D/g, '').substring(0, 2);
    }
    
    if (y && m && d) {
       if (y.length < 4) y = String(currentYear); 
       return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return '';
  };

  const parseClockCSV = (text, fallbackYear) => {
    const lines = text.split(/\r?\n/);
    const results = [];

    let currentCode = '';
    let currentName = '';
    let headersFound = false;
    let idx = {
      date: 2,
      rstStart: 3,
      rstEnd: 4,
      astStart: 5,
      astEnd: 6,
      late: 7,
      early: 8,
      absent: 9,
      leaveStart: 10,
      leaveEnd: 11,
      leaveType: 12,
    };

    const dateRegex = /^(?:\d{2,4}[\/\-])?\d{1,2}[\/\-]\d{1,2}/;

    for (const line of lines) {
      const cleanLine = line.replace(/["\r]/g, '');
      if (!cleanLine.trim()) continue;

      const separator = cleanLine.includes('\t') ? '\t' : ',';
      const cells = cleanLine.split(separator).map(c => c.trim());

      if (!headersFound && cells.some(c => c.includes('實際出勤') || c.includes('上班時間') || c.includes('實際上班') || c.includes('簽到'))) {
        headersFound = true;
        const findIdx = (keywords, fallback) => {
          const found = cells.findIndex(c => keywords.some(k => c.includes(k)));
          return found !== -1 ? found : fallback;
        };

        idx = {
          date: findIdx(['日期', '差勤日'], idx.date),
          rstStart: findIdx(['應出勤', '預定上班'], idx.rstStart),
          rstEnd: findIdx(['應退勤', '預定下班'], idx.rstEnd),
          astStart: findIdx(['上班時間', '實際上班', '簽到', '實際出勤'], idx.astStart),
          astEnd: findIdx(['下班時間', '實際下班', '簽退', '實際退勤'], idx.astEnd),
          late: findIdx(['遲到'], idx.late),
          early: findIdx(['早退'], idx.early),
          absent: findIdx(['曠職', '缺勤'], idx.absent),
          leaveStart: findIdx(['請假開始時間'], idx.leaveStart),
          leaveEnd: findIdx(['請假結束時間'], idx.leaveEnd),
          leaveType: findIdx(['假別'], idx.leaveType),
        };
        continue;
      }

      const codeCell = cells.find(c => c.includes('編號') || c.includes('代號') || c.includes('工號'));
      if (codeCell && (codeCell.includes(':') || codeCell.includes('：'))) {
        const parts = codeCell.split(/[:：]/);
        if (parts.length > 1) currentCode = parts[1].trim();
      }

      const nameCell = cells.find(c => c.includes('姓名'));
      if (nameCell && (nameCell.includes(':') || nameCell.includes('：'))) {
        const parts = nameCell.split(/[:：]/);
        if (parts.length > 1) currentName = parts[1].trim();
      }

      let currentLineDateIdx = idx.date;
      if (!cells[currentLineDateIdx] || !dateRegex.test(cells[currentLineDateIdx])) {
        const altIdx = cells.findIndex(c => dateRegex.test(c));
        if (altIdx !== -1) currentLineDateIdx = altIdx;
      }

      if (cells[currentLineDateIdx] && dateRegex.test(cells[currentLineDateIdx])) {
        const cleanDate = extractCleanDate(cells[currentLineDateIdx], fallbackYear);
        if (!cleanDate) continue;

        results.push({
          Code: currentCode,
          Name: currentName,
          Sdate: cleanDate,
          RSTime_start: formatTimeStr(cells[idx.rstStart]),
          RSTime_end: formatTimeStr(cells[idx.rstEnd]),
          ASTime_start: formatTimeStr(cells[idx.astStart]),
          ASTime_end: formatTimeStr(cells[idx.astEnd]),
          LateMins: toNumber(cells[idx.late]),
          EarlyMins: toNumber(cells[idx.early]),
          AbsentMins: toNumber(cells[idx.absent]),
          LeaveStart: formatTimeStr(cells[idx.leaveStart]),
          LeaveEnd: formatTimeStr(cells[idx.leaveEnd]),
          LeaveType: String(cells[idx.leaveType] || '').trim(),
        });
      }
    }
    return results;
  };

  const parseBorgCSV = (text, fallbackYear) => {
    const lines = text.split(/\r?\n/);
    const results = [];
    let isData = false;
    let idx = {
      code: 0,
      name: 1,
      date: 2,
      rstStart: 3,
      rstEnd: 4,
      astStart: 5,
      astEnd: 6,
      late: 7,
      early: 8,
      absent: 9,
      leaveStart: 10,
      leaveEnd: 11,
      leaveType: 12,
    };

    const findIdx = (headerCells, keywords, fallback) => {
      const found = headerCells.findIndex(c => keywords.some(k => String(c || '').includes(k)));
      return found !== -1 ? found : fallback;
    };

    for (const line of lines) {
      const cleanLine = line.replace(/["\r]/g, '');
      if (!cleanLine.trim()) continue;
      const separator = cleanLine.includes('\t') ? '\t' : ',';
      const cells = cleanLine.split(separator).map(c => c.trim());

      const isHeaderRow = cells.some(c => c.includes('員工代號') || c.includes('工號') || c.includes('代號')) && cells.some(c => c.includes('員工姓名') || c.includes('姓名'));
      if (!isData && isHeaderRow) {
        isData = true;
        idx = {
          code: findIdx(cells, ['員工代號', '工號', '代號'], idx.code),
          name: findIdx(cells, ['員工姓名', '姓名'], idx.name),
          date: findIdx(cells, ['差勤日', '日期'], idx.date),
          rstStart: findIdx(cells, ['應出勤', '預定上班'], idx.rstStart),
          rstEnd: findIdx(cells, ['應退勤', '預定下班'], idx.rstEnd),
          astStart: findIdx(cells, ['實際出勤', '上班時間', '實際上班', '簽到'], idx.astStart),
          astEnd: findIdx(cells, ['實際退勤', '下班時間', '實際下班', '簽退'], idx.astEnd),
          late: findIdx(cells, ['遲到'], idx.late),
          early: findIdx(cells, ['早退'], idx.early),
          absent: findIdx(cells, ['曠職', '缺勤'], idx.absent),
          leaveStart: findIdx(cells, ['請假開始時間'], idx.leaveStart),
          leaveEnd: findIdx(cells, ['請假結束時間'], idx.leaveEnd),
          leaveType: findIdx(cells, ['假別'], idx.leaveType),
        };
        continue;
      }

      if (!isData) continue;
      if (cells.every(c => !c)) continue;

      const code = String(cells[idx.code] || '').trim();
      const name = String(cells[idx.name] || '').trim();
      const rawDate = String(cells[idx.date] || '').trim();
      if (!code || !rawDate) continue;

      const formattedDate = extractCleanDate(rawDate, fallbackYear);
      if (!formattedDate) continue;

      results.push({
        Code: code,
        Name: name,
        Sdate: formattedDate,
        RSTime_start: formatTimeStr(cells[idx.rstStart]),
        RSTime_end: formatTimeStr(cells[idx.rstEnd]),
        ASTime_start: formatTimeStr(cells[idx.astStart]),
        ASTime_end: formatTimeStr(cells[idx.astEnd]),
        LateMins: toNumber(cells[idx.late]),
        EarlyMins: toNumber(cells[idx.early]),
        AbsentMins: toNumber(cells[idx.absent]),
        LeaveStart: formatTimeStr(cells[idx.leaveStart]),
        LeaveEnd: formatTimeStr(cells[idx.leaveEnd]),
        LeaveType: String(cells[idx.leaveType] || '').trim(),
      });
    }
    return results;
  };

  const processFile = (selectedFile, forcedEnc, currentFormat) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const buffer = event.target.result;
        let text = '';
        if (forcedEnc === 'auto') {
          try {
            const utf8Decoder = new TextDecoder('utf-8', { fatal: true });
            text = utf8Decoder.decode(buffer);
            if (!text.includes('員工') && !text.includes('姓名') && !text.includes('日期') && !text.includes('編號')) {
              const big5Decoder = new TextDecoder('big5');
              const big5Text = big5Decoder.decode(buffer);
              if (big5Text.includes('員工') || big5Text.includes('姓名') || big5Text.includes('日期') || big5Text.includes('編號')) {
                text = big5Text; setEncoding('big5');
              } else setEncoding('utf-8');
            } else setEncoding('utf-8');
          } catch {
            const big5Decoder = new TextDecoder('big5');
            text = big5Decoder.decode(buffer); setEncoding('big5');
          }
        } else {
          const decoder = new TextDecoder(forcedEnc); text = decoder.decode(buffer);
        }

        let rawData = [];
        if (currentFormat === 'borg') {
            rawData = parseBorgCSV(text, defaultYear);
        } else {
            rawData = parseClockCSV(text, defaultYear);
        }

        const uniqueMap = new Map();
        rawData
          .filter((row) => row.Code && row.Sdate)
          .forEach((row) => {
            const key = `${row.Code}_${row.Sdate}`;
            
            if (uniqueMap.has(key)) {
              const existing = uniqueMap.get(key);
              const existingScore = (existing.ASTime_start ? 1 : 0) + (existing.ASTime_end ? 1 : 0) + (existing.AbsentMins > 0 ? 1 : 0);
              const newScore = (row.ASTime_start ? 1 : 0) + (row.ASTime_end ? 1 : 0) + (row.AbsentMins > 0 ? 1 : 0);
              if (newScore > existingScore) {
                uniqueMap.set(key, row);
              }
            } else {
              uniqueMap.set(key, row);
            }
          });

        const finalData = Array.from(uniqueMap.values());
        
        // ==========================================
        // 💡 修正 3：智慧校正單次打卡錯位問題
        // ==========================================
        finalData.forEach(row => {
            let inTime = row.ASTime_start;
            let outTime = row.ASTime_end;
    
            if (inTime && !outTime) {
                if (inTime >= '12:00:00') { 
                    row.ASTime_end = inTime;
                    row.ASTime_start = '';
                }
            } else if (!inTime && outTime) {
                if (outTime < '12:00:00') {
                    row.ASTime_start = outTime;
                    row.ASTime_end = '';
                }
            } else if (inTime && outTime) {
                if (inTime > outTime) {
                    const temp = row.ASTime_start;
                    row.ASTime_start = row.ASTime_end;
                    row.ASTime_end = temp;
                }
            }
        });

        if (finalData.length === 0) setErrorMsg(`無法解析出有效打卡資料，或檔案中所有員工皆無出勤紀錄。`);
        else if (finalData.some((d) => !d.Code || !d.Name)) setErrorMsg('⚠️ 系統已讀取打卡時間，但部分「員工代號」或「姓名」未能成功擷取，請檢查檔案格式。');
        else setErrorMsg('');

        setRawParsedData(finalData);
      } catch (err) { setErrorMsg('檔案讀取失敗：' + err.message); }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleFileChange = (e, format) => {
    setErrorMsg('');
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFormatType(format);
      setFile(selectedFile); 
      processFile(selectedFile, encoding, format);
    }
  };

  const handleEncodingChange = (e) => {
    const newEnc = e.target.value; setEncoding(newEnc);
    if (file) processFile(file, newEnc, formatType);
  };

  const generateCleanCSV = (data) => {
    if (data.length === 0) return '';
    const header = 'Sdate,Code,RSTime_start,RSTime_end,ASTime_start,ASTime_end,LateMins,EarlyMins,AbsentMins,LeaveStart,LeaveEnd,LeaveType,Rcode';
    const rows = data.filter((d) => d.Code && d.Code.trim() !== '' && d.Sdate).map((obj) => {
      const sanitize = (str) => String(str || '').replace(/[\r\n,"]/g, ' ').trim();
      return [sanitize(obj.Sdate), sanitize(obj.Code), sanitize(obj.RSTime_start), sanitize(obj.RSTime_end), sanitize(obj.ASTime_start), sanitize(obj.ASTime_end), obj.LateMins || 0, obj.EarlyMins || 0, obj.AbsentMins || 0, sanitize(obj.LeaveStart), sanitize(obj.LeaveEnd), sanitize(obj.LeaveType), sanitize(obj.Rcode)].join(',');
    });
    return [header, ...rows].join('\n');
  };

  const handleUpload = async () => {
    const uploadableRows = parsedData.filter((row) => !isApprovedLeaveNoPunchRow(row));
    if (uploadableRows.length === 0) { alert('沒有可匯入的資料'); return; }
    setIsUploading(true);
    try {
      let corpSno = 1;
      const fileName = file?.name || '';
      if (fileName.includes('三禾聯')) corpSno = 2;
      else if (fileName.includes('昭雄')) corpSno = 1;

      // 💡 準備上傳資料時，直接使用已過濾和計算好的資料
      const records = uploadableRows.map((row) => {
        return {
          CorpSno: corpSno, 
          Code: String(row.Code || '').trim(), 
          Sdate: String(row.Sdate || '').trim(),
          RSTime_start: row.RSTime_start ? String(row.RSTime_start).trim() : null,
          RSTime_end: row.RSTime_end ? String(row.RSTime_end).trim() : null,
          ASTime_start: row.ASTime_start ? String(row.ASTime_start).trim() : null,
          ASTime_end: row.ASTime_end ? String(row.ASTime_end).trim() : null,
          AbsentMins: row.AbsentMins || 0,
          Rcode: row.Rcode || null
        };
      });

      const batchId = crypto.randomUUID();
      
      const response = await fetch(`${API_BASE_URL}/upload-attendance`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ BatchId: batchId, records })
      });

      const rawText = await response.text();
      let result = {};
      try { result = rawText ? JSON.parse(rawText) : {}; } catch { throw new Error('n8n 回傳的不是合法 JSON：' + rawText); }

      if (!response.ok || result.success === false) throw new Error(result.message || `伺服器回應代碼: ${response.status}`);

      const insertedCount = Number(result.insertedCount ?? result.inserted_count ?? 0);
      const updatedCount = Number(result.updatedCount ?? result.updated_count ?? 0);
      const totalCount = Number(result.totalCount ?? result.total_count ?? 0);
      const displayTotal = totalCount || insertedCount + updatedCount || records.length;
      const successMessage = displayTotal > 0
        ? `🎉 匯入完成，新增 ${insertedCount} 筆，更新 ${updatedCount} 筆，共 ${displayTotal} 筆資料`
        : (result.message || `🎉 成功匯入 ${records.length} 筆資料`);

      alert(successMessage);
      setFile(null); setRawParsedData([]); setErrorMsg(''); setShowDebug(false);
      if (setIsDirty) setIsDirty(false);
      
      if (onImportSuccess) onImportSuccess();

    } catch (err) { 
      alert('上傳發生錯誤：\n\n' + err.message); 
    } finally { 
      setIsUploading(false); 
    }
  };

  const userStatusList = useMemo(() => {
    if (!db?.GSUSER) return [];
    
    return db.GSUSER.map(user => {
      const userId = String(pick(user, ['UserId', 'Code'])).trim().toUpperCase();
      const records = (db.PERREC || []).filter(r =>
        String(pick(r, ['Code', 'UserId'])).trim().toUpperCase() === userId &&
        normalizeDate(pick(r, ['Sdate'])).startsWith(exportMonth)
      );

      let statusObj = { code: 'B', label: '無須申請', color: 'text-slate-600 bg-slate-100', badgeColor: 'bg-slate-100 text-slate-600 border-slate-200' };

      if (records.length === 0) {
        statusObj = { code: 'A', label: '無出勤資料', color: 'text-slate-400 bg-slate-50', badgeColor: 'bg-slate-50 text-slate-500 border-slate-200' };
      } else {
        let hasUnapplied = false;
        let hasPending = false;
        let hasApproved = false;

        records.forEach(r => {
          const rawRcode = String(r._rawRcode || r.Rcode || '');
          const mergedRcode = String(r.Rcode || '');
          
          const isMissed = rawRcode.includes('未打卡') || rawRcode.includes('未刷卡');
          const isRawPending = rawRcode.includes('審核中') || rawRcode.includes('待審核中');
          const isRawApproved = rawRcode.includes('已核准') || rawRcode.includes('已審核');

          const isMergedPending = mergedRcode.includes('審核中') || mergedRcode.includes('待審核中');
          const isMergedApproved = mergedRcode.includes('已核准') || mergedRcode.includes('已審核');

          if (isMissed || isRawPending || isRawApproved) {
            if (isMergedApproved || isRawApproved) {
              hasApproved = true;
            } else if (isMergedPending || isRawPending) {
              hasPending = true;
            } else {
              const cvertcheck = String(pick(r, ['Cvertcheck', 'CvertCheck'], '')).toUpperCase();
              const isCvertchecked = cvertcheck === 'Y' || cvertcheck === '1' || cvertcheck === 'TRUE' || pick(r, ['Cvertcheck']) === 1 || pick(r, ['Cvertcheck']) === true;
              if (!isCvertchecked) {
                hasUnapplied = true;
              }
            }
          }
        });

        if (hasUnapplied) {
          statusObj = { code: 'C', label: '未申請', color: 'text-rose-500 bg-rose-50', badgeColor: 'bg-rose-50 text-rose-600 border-rose-200' };
        } else if (hasPending) {
          statusObj = { code: 'D', label: '待審核中', color: 'text-blue-600 bg-blue-50', badgeColor: 'bg-blue-50 text-blue-600 border-blue-200' };
        } else if (hasApproved) {
          statusObj = { code: 'E', label: '已核准', color: 'text-green-600 bg-green-50', badgeColor: 'bg-green-50 text-green-600 border-green-200' };
        }
      }

      return {
        userId,
        name: user.Name,
        dept: db.GSDEPT?.find((d) => d.Code === user.Deptcode)?.Name || user.Deptcode,
        status: statusObj
      };
    });
  }, [db?.GSUSER, db?.PERREC, db?.GSDEPT, exportMonth]);

  const handleExportToggleUser = (userId) => {
    setSelectedUsersExport(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleExportExcel = async () => {
    if (selectedUsersExport.length === 0) {
      alert("請先勾選要匯出的員工資料！");
      return;
    }

    try {
      const XLSX = await loadXLSX();
      const wb = XLSX.utils.book_new();

      const selectedData = userStatusList.filter(u => selectedUsersExport.includes(u.userId));

      selectedData.forEach(user => {
        const userRecords = (db.PERREC || [])
          .filter(r => String(pick(r, ['Code'])).trim() === user.userId && normalizeDate(pick(r, ['Sdate'])).startsWith(exportMonth))
          .sort((a, b) => new Date(normalizeDate(pick(a, ['Sdate']))) - new Date(normalizeDate(pick(b, ['Sdate']))));

        const sheetData = userRecords.map(r => ({
          '日期': normalizeDate(pick(r, ['Sdate'])),
          '應出勤': normalizeTime(pick(r, ['RSTimestart', 'RSTime_start'])),
          '應退勤': normalizeTime(pick(r, ['RSTimeend', 'RSTime_end'])),
          '實際出勤': normalizeTime(pick(r, ['ASTimestart', 'ASTime_start'])),
          '實際退勤': normalizeTime(pick(r, ['ASTimeend', 'ASTime_end'])),
          '異常狀態': pick(r, ['Rcode']),
          '上班補登時間': pick(r, ['NoSwipe_start', 'MakeupTime_start', '_makeupTimeStart', 'Makeup_In']) || '',
          '下班補登時間': pick(r, ['NoSwipe_end', 'MakeupTime_end', '_makeupTimeEnd', 'Makeup_Out']) || '',
          '未打卡事由': pick(r, ['_reason', 'Reason', 'Note']) || ''
        }));

        if (sheetData.length === 0) {
          sheetData.push({ '日期': '當月無出勤資料' });
        }

        const ws = XLSX.utils.json_to_sheet(sheetData);
        ws['!cols'] = [{wch: 12}, {wch: 10}, {wch: 10}, {wch: 10}, {wch: 10}, {wch: 15}, {wch: 14}, {wch: 14}, {wch: 25}];
        
        let sheetName = `${user.userId}-${user.name}`;
        if (sheetName.length > 31) sheetName = sheetName.substring(0, 31); 
        
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      XLSX.writeFile(wb, `員工補打卡審核資料_${exportMonth}.xlsx`);
    } catch (err) {
      console.error(err);
      alert("匯出失敗，請稍後再試或聯繫管理員：" + err.message);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4 sm:gap-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2">
            <Upload size={22} className="text-slate-500" /> E-1. 智能打卡資料匯入
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
             <div className="flex items-center gap-2 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 sm:py-1.5 text-sm w-full sm:w-auto">
                <span className="text-slate-600 font-medium whitespace-nowrap">預設年份：</span>
                <input 
                  type="number" 
                  className="w-16 bg-transparent outline-none font-bold text-blue-600"
                  value={defaultYear}
                  onChange={(e) => setDefaultYear(e.target.value)}
                />
             </div>
             <select value={encoding} onChange={handleEncodingChange} className="w-full sm:w-auto border border-slate-300 rounded-lg px-4 py-2 sm:py-1.5 text-sm outline-none focus:border-blue-500 bg-slate-50 text-slate-700 font-medium cursor-pointer">
               <option value="auto">編碼: 自動偵測 (推薦)</option>
               <option value="big5">編碼: Big5 (傳統機型)</option>
               <option value="utf-8">編碼: UTF-8 (現代系統)</option>
             </select>
          </div>
        </div>

        <div className="text-xs sm:text-sm text-slate-500 mb-4 bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-100 flex flex-col gap-3">
          <div className="flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">⚡</span> <strong>系統支援自動清洗</strong>：點擊對應版本並上傳原始 CSV，系統會自動轉換成乾淨的扁平資料表。
          </div>
          <div className="flex items-center gap-2 bg-white/60 p-2 rounded border border-blue-200/60 w-fit">
            <input 
              type="checkbox" 
              checked={filterNoDataUser} 
              onChange={(e) => setFilterNoDataUser(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
              id="filterNoData"
            />
            <label htmlFor="filterNoData" className="font-medium text-slate-700 cursor-pointer select-none">
              自動剃除「整份檔案皆無打卡紀錄」之員工 (預設關閉，確保忘記打卡也能補登)
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border-2 border-dashed border-blue-300 rounded-xl p-6 sm:p-8 flex flex-col items-center justify-center text-slate-500 bg-blue-50 relative transition-colors hover:bg-blue-100 cursor-pointer group">
            <FileDown size={40} className="text-blue-400 group-hover:text-blue-600 transition-colors mb-3" />
            <p className="mb-1 text-blue-800 font-bold text-center">匯入出勤 (新光版)</p>
            <p className="text-xs text-blue-600 mb-3 text-center">適用：原打卡系統資料</p>
            <p className="text-sm text-blue-700 font-bold truncate max-w-full px-4">{formatType === 'shinkong' && file ? file.name : '點選上傳 CSV 檔'}</p>
            <input type="file" accept=".csv" onChange={(e) => handleFileChange(e, 'shinkong')} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
          </div>

          <div className="border-2 border-dashed border-emerald-300 rounded-xl p-6 sm:p-8 flex flex-col items-center justify-center text-slate-500 bg-emerald-50 relative transition-colors hover:bg-emerald-100 cursor-pointer group">
            <FileDown size={40} className="text-emerald-400 group-hover:text-emerald-600 transition-colors mb-3" />
            <p className="mb-1 text-emerald-800 font-bold text-center">匯入出勤 (博格版)</p>
            <p className="text-xs text-emerald-600 mb-3 text-center">適用：新平好室社宅資料</p>
            <p className="text-sm text-emerald-700 font-bold truncate max-w-full px-4">{formatType === 'borg' && file ? file.name : '點選上傳 CSV 檔'}</p>
            <input type="file" accept=".csv" onChange={(e) => handleFileChange(e, 'borg')} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
          </div>
        </div>

        {errorMsg && <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg border border-red-200 text-sm font-medium">{errorMsg}</div>}
      </div>

      {parsedData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 flex flex-col overflow-hidden min-h-[400px]">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-4 shrink-0 border-b border-slate-100 pb-4">
            <div>
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} /> 資料清洗預覽
              </h3>
              <p className="text-sm text-slate-500 mt-1.5">
                成功解析出 <strong className="text-blue-600 text-lg mx-1">{parsedData.length}</strong> 筆有效打卡記錄
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <button onClick={() => setShowDebug(!showDebug)} className="text-sm text-slate-500 hover:text-slate-800 flex items-center justify-center gap-1.5 transition-colors py-2 sm:py-0 border border-slate-200 sm:border-transparent rounded-lg sm:rounded-none">
                <Code size={16} /> 檢查透視鏡
              </button>
              <button onClick={handleUpload} disabled={isUploading} className={`w-full sm:w-auto px-6 py-3 sm:py-2.5 rounded-lg shadow-sm font-bold transition-all ${isUploading ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700 active:scale-[0.98]'}`}>
                {isUploading ? '處理並上傳中...' : '確認無誤，匯入資料庫'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto w-full border rounded-lg border-slate-200 flex-1 custom-scrollbar">
            {showDebug ? (
              <div className="p-4 text-xs font-mono text-slate-600 bg-slate-50 whitespace-pre-wrap overflow-x-auto">
                <div className="text-blue-600 font-bold mb-3 border-b border-blue-200 pb-2">
                  ⬇️ 以下為傳送給 n8n 的純文字格式預覽 (已包含曠職判定)：
                </div>
                {generateCleanCSV(parsedData.slice(0, 10)) + '\n... (其餘省略)'}
              </div>
            ) : (
              <table className="w-full text-left border-collapse min-w-[700px]">
                <thead className="sticky top-0 bg-slate-100 shadow-sm z-10">
                  <tr className="text-slate-600 text-sm">
                    <th className="py-2.5 px-4 font-semibold border-b whitespace-nowrap">員工代號</th>
                    <th className="py-2.5 px-4 font-semibold border-b whitespace-nowrap">員工姓名</th>
                    <th className="py-2.5 px-4 font-semibold border-b whitespace-nowrap">日期</th>
                    <th className="py-2.5 px-4 font-semibold border-b text-slate-400 whitespace-nowrap">應出勤</th>
                    <th className="py-2.5 px-4 font-semibold border-b text-slate-400 whitespace-nowrap">應退勤</th>
                    <th className="py-2.5 px-4 font-semibold border-b text-blue-700 whitespace-nowrap">實際出勤</th>
                    <th className="py-2.5 px-4 font-semibold border-b text-blue-700 whitespace-nowrap">實際退勤</th>
                    <th className="py-2.5 px-4 font-semibold border-b text-rose-600 whitespace-nowrap">曠職(分)</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 50).map((row, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 text-sm transition-colors">
                      <td className="py-2.5 px-4 text-slate-700 font-medium whitespace-nowrap">{row.Code || <span className="text-red-400">未抓到</span>}</td>
                      <td className="py-2.5 px-4 text-slate-700 whitespace-nowrap">{row.Name || <span className="text-red-400">未抓到</span>}</td>
                      <td className="py-2.5 px-4 text-slate-700 whitespace-nowrap">{row.Sdate}</td>
                      <td className="py-2.5 px-4 text-slate-400 font-mono whitespace-nowrap">{row.RSTime_start}</td>
                      <td className="py-2.5 px-4 text-slate-400 font-mono whitespace-nowrap">{row.RSTime_end}</td>
                      <td className="py-2.5 px-4 text-blue-700 font-medium font-mono whitespace-nowrap">{row.ASTime_start || <span className="text-slate-300">無紀錄</span>}</td>
                      <td className="py-2.5 px-4 text-blue-700 font-medium font-mono whitespace-nowrap">{row.ASTime_end || <span className="text-slate-300">無紀錄</span>}</td>
                      <td className="py-2.5 px-4 text-rose-600 font-medium font-mono whitespace-nowrap">
                        {row.AbsentMins > 0 ? (
                          <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded text-xs">{row.AbsentMins}</span>
                        ) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!showDebug && parsedData.length > 50 && (
              <div className="text-center text-sm text-slate-500 py-4 bg-slate-50 border-t border-slate-100">(僅顯示前 50 筆供預覽...)</div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6 mb-8 flex flex-col flex-1">
        <div className="flex flex-col lg:flex-row gap-6 mb-4">
          <div className="flex-1 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2 shrink-0">
                E-2. 員工補打卡審核完成資料匯出
              </h2>
              
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <select 
                  value={exportMonth} 
                  onChange={(e) => setExportMonth(e.target.value)}
                  className="w-full sm:w-auto border border-slate-300 rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm font-medium"
                >
                  {displayExportMonths.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <button
                  onClick={handleExportExcel}
                  className="w-full sm:w-auto px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm"
                >
                  <FileDown size={16} /> 匯出 Excel
                </button>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden flex flex-col min-h-[300px]">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 items-center">
                <div className="col-span-1 text-center">選取</div>
                <div className="col-span-2">員工代號</div>
                <div className="col-span-3">姓名</div>
                <div className="col-span-3">所屬部門</div>
                <div className="col-span-3 text-center">本月狀態</div>
              </div>
              <div className="overflow-y-auto custom-scrollbar flex-1 bg-white max-h-[400px]">
                {userStatusList.map((user) => {
                  const isSelected = selectedUsersExport.includes(user.userId);
                  return (
                    <label 
                      key={user.userId} 
                      className={`px-4 py-2.5 border-b border-slate-100 grid grid-cols-12 gap-2 items-center cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}
                    >
                      <div className="col-span-1 flex justify-center text-slate-400">
                        <input 
                          type="checkbox" 
                          className="sr-only" 
                          checked={isSelected}
                          onChange={() => handleExportToggleUser(user.userId)}
                        />
                        {isSelected ? (
                          <CheckSquare className="text-blue-600" size={18} />
                        ) : (
                          <Square size={18} />
                        )}
                      </div>
                      <div className="col-span-2 text-sm text-slate-700 font-medium">{user.userId}</div>
                      <div className="col-span-3 text-sm text-slate-700">{user.name}</div>
                      <div className="col-span-3 text-sm text-slate-500 truncate">{user.dept}</div>
                      <div className="col-span-3 text-center">
                        <span className={`text-[11px] px-2.5 py-1 rounded-full border ${user.status.badgeColor} font-medium`}>
                          {user.status.label}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="w-full lg:w-64 shrink-0 bg-slate-50 p-4 sm:p-5 rounded-lg border border-slate-200">
            <h4 className="font-bold text-slate-700 text-sm mb-3 border-b border-slate-200 pb-2">狀態圖例說明</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-300 mt-1.5 shrink-0"></span>
                <div>
                  <div className="font-bold text-slate-500 mb-0.5">A.無出勤資料</div>
                  <div className="text-slate-400 text-xs">表示該員工本月無任何打卡紀錄</div>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-600 mt-1.5 shrink-0"></span>
                <div>
                  <div className="font-bold text-slate-700 mb-0.5">B.無須申請</div>
                  <div className="text-slate-500 text-xs">表示該員工【無】未打卡資料</div>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500 mt-1.5 shrink-0"></span>
                <div>
                  <div className="font-bold text-rose-600 mb-0.5">C.未申請</div>
                  <div className="text-slate-500 text-xs">表示該員工【有】未打卡資料，但【未提出】補打卡申請</div>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-1.5 shrink-0"></span>
                <div>
                  <div className="font-bold text-blue-600 mb-0.5">D.待審核中</div>
                  <div className="text-slate-500 text-xs">表示該員工【已提出】補打卡申請，但主管尚未核准</div>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500 mt-1.5 shrink-0"></span>
                <div>
                  <div className="font-bold text-green-600 mb-0.5">E.已核准</div>
                  <div className="text-slate-500 text-xs">表示該員工之補打卡單【皆已核准】</div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
